import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from "react";
import {
  ChartBarIcon,
  DocumentTextIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  UserIcon,
  UserPlusIcon,
  SunIcon,
  CurrencyDollarIcon,
  UsersIcon,
  Cog6ToothIcon
} from "@heroicons/react/24/outline";
import { TbLayoutSidebarRightExpand, TbLayoutSidebarRightCollapse } from "react-icons/tb";
import { LuFolderPlus, LuFolders, LuUsers } from "react-icons/lu";
import { FaPlus, FaSuperpowers } from "react-icons/fa6";
import { NavButton } from "./NavButton";
import { UserProfile } from "./UserProfile";
import nvgtrLogo from "../../assets/nvgtrLogo_Black.svg";
import nvgtrLogoDark from "../../assets/nvgtrLogo_White.svg";
import nvgtrBM from "../../assets/nvgtr_BM.svg";
import nvgtrBMDark from "../../assets/nvgtr_BM_white.svg";
import axios from 'axios';
import Modal from '../common/Modal';
import ContextMenu from '../common/ContextMenu';
import CreateProjectModal from '../projects/CreateProjectModal';
import CreateClientModal from '../clients/CreateClientModal';
import SearchModal from './SearchModal';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { debounce } from 'lodash';
import { useInView } from 'react-intersection-observer';
import throttle from 'lodash/throttle';
import { formatChatDate, groupChatsByDate } from '../../utils/date';
import { useTheme } from '../../features/theme/context/ThemeContext';
import { useS3Image } from '../../hooks/useS3Image';
import { mergeWithTestData, getTestProjects, logTestConfig } from '../../utils/testData';
import { useWebSocketManager } from '../../hooks/useWebSocketManager';
import { canAccessProjects } from '../../utils/planPermissions';

// Custom CSS for scrollbar behavior
import './Sidebar.css';
// Animation styles for new chats
import './sidebar-animations.css';
import { getBrandAssetsCached } from '../../services/brandAssets';

// Simple in-memory cache to replace the complex messageManager
const simpleCache = new Map();

// Helper function to get headers with test mode support
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    Authorization: `Bearer ${token}`
  };
  
  // 🛠️ Development Test User/Org Override
  if (process.env.NODE_ENV === 'development') {
    const testOrgMode = localStorage.getItem('devTestOrgMode');
    const testOrgId = localStorage.getItem('devTestOrgId');
    const testUserId = localStorage.getItem('devActiveTestUserId');
    
    if (testOrgMode === 'true' && testOrgId) {
      headers['X-Test-Org-Id'] = testOrgId;
    }
    
    if (testOrgMode === 'true' && testUserId) {
      headers['X-Test-User-Id'] = testUserId;
    }
  }
  
  return headers;
};

// Improved proxy URL function to better handle S3 URLs
const getProxiedImageUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // If it's already not an S3 URL, just return it as is
  if (!s3Url.includes('amazonaws.com')) {
    return s3Url;
  }
  
  // Extract file key from the S3 URL (everything after the bucket name)
  const urlParts = s3Url.split('/');
  const fileKey = urlParts.slice(3).join('/');
  
  // Return URL that will be handled by our backend proxy endpoint
  return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
};

// Logo spinner component - themed gray spinner
const LogoSpinner = ({ darkMode }) => {
  return (
    <div className="h-8 w-8 flex items-center justify-center">
      <div 
        className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
        style={{
          borderColor: darkMode ? '#444444' : '#c3c3c3',
          borderTopColor: 'transparent'
        }}
      />
    </div>
  );
};

// Add the MemberAvatarStack component after the imports and before the main component
const MemberAvatarStack = ({ members = [], memberCount = 0, maxVisible = 3, projectId, projectTypingUsers = {}, projectRecentlyActive = {} }) => {
  // Updated color palette with new hex values
  const pastelColors = [
    { bg: 'bg-[#F5C8BD]', hex: '#F5C8BD' },
    { bg: 'bg-[#E9BACC]', hex: '#E9BACC' },
    { bg: 'bg-[#D0AAB0]', hex: '#D0AAB0' },
    { bg: 'bg-[#B599C1]', hex: '#B599C1' },
    { bg: 'bg-[#ABACCB]', hex: '#ABACCB' },
    { bg: 'bg-[#9FBFDD]', hex: '#9FBFDD' },
    { bg: 'bg-[#97CCDC]', hex: '#97CCDC' },
    { bg: 'bg-[#83C4B4]', hex: '#83C4B4' },
    { bg: 'bg-[#A9D194]', hex: '#A9D194' },
    { bg: 'bg-[#D5E4AD]', hex: '#D5E4AD' },
    { bg: 'bg-[#FAEEAE]', hex: '#FAEEAE' },
    { bg: 'bg-[#F5BE6B]', hex: '#F5BE6B' }
  ];

  // Function to generate a darker text color based on the background color
  const getDarkerTextColor = (hexColor) => {
    // Remove the # if present
    const hex = hexColor.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Generate darker versions (reduce each component by about 40-60%)
    const darkerR = Math.max(0, Math.floor(r * 0.4));
    const darkerG = Math.max(0, Math.floor(g * 0.4));
    const darkerB = Math.max(0, Math.floor(b * 0.4));
    
    // Convert back to hex
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(darkerR)}${toHex(darkerG)}${toHex(darkerB)}`;
  };

  // Simple hash function to consistently assign colors based on user ID
  const getUserColor = (userId) => {
    if (!userId) return pastelColors[0];
    
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return pastelColors[Math.abs(hash) % pastelColors.length];
  };

  // Get current user ID from token to exclude from display
  let currentUserId = null;
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      currentUserId = payload.userId || payload.id;
    }
  } catch (error) {
    console.error('Error decoding token:', error);
  }
  
  // Filter out current user from members
  const filteredMembers = members.filter(member => member.id !== currentUserId);
  // Adjust member count to exclude current user
  const adjustedMemberCount = currentUserId ? Math.max(0, memberCount - 1) : memberCount;
  
  // Get typing users for this project
  const typingUsers = projectTypingUsers[projectId] || {};
  const typingUserIds = Object.keys(typingUsers);
  const recentlyActiveUsers = projectRecentlyActive[projectId] || {};
  
  // Create a combined list prioritizing typing users, then recently active users, then regular members
  const allRelevantUsers = new Map(); // userId -> userInfo
  
  // Add typing users (highest priority - they go to front and get typing animation)
  typingUserIds.forEach(userId => {
    const typingUser = typingUsers[userId];
    allRelevantUsers.set(userId, {
      id: userId,
      userName: typingUser.userName,
      userColor: typingUser.userColor,
      profileImage: typingUser.profileImage,
      isTyping: true,
      priority: 1
    });
  });
  
  // Add recently active users (medium priority - they stay at front after typing)
  Object.keys(recentlyActiveUsers).forEach(userId => {
    if (!allRelevantUsers.has(userId)) {
      const recentUser = recentlyActiveUsers[userId];
      allRelevantUsers.set(userId, {
        id: userId,
        userName: recentUser.userName,
        userColor: recentUser.userColor,
        profileImage: recentUser.profileImage,
        isTyping: false,
        priority: 2,
        lastActiveTime: recentUser.lastActiveTime
      });
    }
  });
  
  // Add regular members (lowest priority)
  filteredMembers.forEach(member => {
    if (!allRelevantUsers.has(member.id)) {
      allRelevantUsers.set(member.id, {
        id: member.id,
        userName: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim(),
        userColor: null, // Will generate from ID
        profileImage: member.profileImage,
        isTyping: false,
        priority: 3
      });
    }
  });
  
  // Sort by priority, then by recent activity time, then by order
  const sortedUsers = Array.from(allRelevantUsers.values()).sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority; // Lower priority number = higher priority
    }
    if (a.lastActiveTime && b.lastActiveTime) {
      return b.lastActiveTime - a.lastActiveTime; // More recent first
    }
    if (a.lastActiveTime && !b.lastActiveTime) return -1;
    if (!a.lastActiveTime && b.lastActiveTime) return 1;
    return 0;
  });
  
  const displayUsers = sortedUsers.slice(0, maxVisible);
  const remainingCount = Math.max(0, adjustedMemberCount - maxVisible);
  
  // Debug logging for typing state
  if (projectId && Object.keys(typingUsers).length > 0) {
    console.log('🎭 MemberAvatarStack debug for project:', projectId, {
      typingUsers: Object.keys(typingUsers),
      recentlyActiveUsers: Object.keys(recentlyActiveUsers),
      displayUsers: displayUsers.map(u => ({ id: u.id, userName: u.userName, isTyping: u.isTyping })),
      sortedUsers: sortedUsers.length
    });
  }
  
  // Generate placeholder initials for demo purposes (only used when no member data exists)
  const getPlaceholderInitials = (index) => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return letters[index % 26] + letters[(index + 1) % 26];
  };

  // Get initials from user data
  const getUserInitials = (user) => {
    if (user.userName) {
      const nameParts = user.userName.trim().split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
      } else {
        return nameParts[0].charAt(0).toUpperCase();
      }
    }
    return null; // Will fall back to placeholder
  };

  if (adjustedMemberCount === 0) return null;

  return (
    <div className="flex items-center">
      {/* Avatar stack with increased overlap */}
      <div className="flex items-center -space-x-2">
        {/* Display user avatars */}
        {displayUsers.map((user, index) => {
          const userInitials = getUserInitials(user);
          const initials = userInitials || getPlaceholderInitials(index);
          
          // Get user color
          const userColor = user.userColor ? { hex: user.userColor } : getUserColor(user.id);
          
          const hasProfileImage = user.profileImage?.url;
          
          return (
            <div
              key={user.id}
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium overflow-hidden relative border-2 border-[#EDEDED] dark:border-[#161616]"
              style={{ zIndex: maxVisible - index }}
              title={`${user.userName}${user.isTyping ? ' (typing...)' : ''}`}
            >
              {hasProfileImage ? (
                // Profile picture with colored outline
                <div className="w-full h-full rounded-full overflow-hidden relative">
                  {/* Colored border background */}
                  <div 
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: userColor.hex }}
                  />
                                            {/* Profile image with slight inset to show the colored border */}
                          <div className="absolute inset-[2px] rounded-full overflow-hidden bg-white dark:bg-[#161616]">
                            <img 
                              src={user.profileImage.url} 
                              alt={user.userName || `User ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Try the proxy URL if the original fails
                                const proxiedUrl = getProxiedImageUrl(user.profileImage.url);
                                if (proxiedUrl && proxiedUrl !== e.target.src) {
                                  console.log('S3 URL failed, trying proxy for user:', user.userName);
                                  e.target.src = proxiedUrl;
                                } else {
                                  // Both failed - hide image
                                  console.error('Both S3 URL and proxy failed for user:', user.userName);
                                  e.target.style.display = 'none';
                                }
                              }}
                            />
                          </div>
                </div>
              ) : (
                // Monogram with colored background
                <div 
                  className={`w-full h-full flex items-center justify-center rounded-full`}
                  style={{ 
                    backgroundColor: userColor.hex,
                    color: getDarkerTextColor(userColor.hex)
                  }}
                >
                  {initials}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Typing indicator dots - show if any user in this project is typing */}
      {typingUserIds.length > 0 && (
        <div className="flex items-center ml-1">
          <div className="flex space-x-0.5">
            {[0, 1, 2].map((dotIndex) => {
              // Use the first typing user's color for the dots
              const firstTypingUser = typingUsers[typingUserIds[0]];
              const userColor = { hex: firstTypingUser.userColor || getUserColor(typingUserIds[0]) };
              return (
                <div
                  key={dotIndex}
                  className="w-1.5 h-1.5 rounded-full animate-bounce"
                  style={{ 
                    backgroundColor: userColor.hex,
                    animationDelay: `${dotIndex * 0.15}s`,
                    animationDuration: '1s'
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
      
      {/* Show +X more if there are additional members - outside the stacking container */}
      {remainingCount > 0 && (
        <div className={`text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 ${typingUserIds.length > 0 ? 'ml-2' : 'ml-1'}`}>
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

export const Sidebar = forwardRef(({ user, setUser, activeTab, setActiveTab, activeChat, onChatSelect, isCollapsed, onToggleCollapse, isAccountModalOpen }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useTheme();
  
  // Internal collapsed state that persists through refresh
  // This overrides the isCollapsed prop to provide localStorage persistence
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Sync internal state when parent collapses/expands the sidebar externally (e.g. auto-collapse)
  useEffect(() => {
    if (typeof isCollapsed === 'boolean' && isCollapsed !== internalIsCollapsed) {
      setInternalIsCollapsed(isCollapsed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  // Handle toggle collapse with localStorage persistence
  const handleToggleCollapse = () => {
    const newCollapsedState = !internalIsCollapsed;
    setInternalIsCollapsed(newCollapsedState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
    
    // Call parent's callback to maintain compatibility
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };
  
  // Use a separate cache key for sidebar to avoid conflicts with chat components
  const [logoUrl, setLogoUrl] = useState(() => {
    // Try to get cached custom logo first, but fallback to default
    try {
      const cachedLogoData = localStorage.getItem('sidebarLogoCache');
      if (cachedLogoData) {
        const { logoUrl, isDark, hasCustomAssets } = JSON.parse(cachedLogoData);
        // Only use cached logo if dark mode matches AND user has custom assets
        if (isDark === darkMode && hasCustomAssets && logoUrl && !logoUrl.includes('nvgtr_BM')) {
          return logoUrl;
        }
      }
    } catch (e) {
      // If any error occurs reading cache, use default
    }
    return darkMode ? nvgtrBMDark : nvgtrBM;
  });
  const [logoLoaded, setLogoLoaded] = useState(() => {
    // If we have any cached logo, consider it loaded
    try {
      const cachedLogoData = localStorage.getItem('sidebarLogoCache');
      if (cachedLogoData) {
        const { isDark, logoUrl } = JSON.parse(cachedLogoData);
        if (isDark === darkMode && logoUrl) {
          return true;
        }
      }
    } catch (e) {
      // If any error occurs reading cache, default to false
    }
    return false;
  });
  
  // Track the cached preference to avoid unnecessary API calls
  const [cachedPreference, setCachedPreference] = useState('brandmark');
  
  // Add state to track logo type for dynamic sizing
  const [isUsingCustomAssets, setIsUsingCustomAssets] = useState(() => {
    try {
      const cachedLogoData = localStorage.getItem('sidebarLogoCache');
      if (cachedLogoData) {
        const { hasCustomAssets, isDark } = JSON.parse(cachedLogoData);
        // Only use cached value if dark mode matches
        if (isDark === darkMode) {
          return hasCustomAssets || false;
        }
      }
    } catch (e) {
      // If any error occurs reading cache, default to false
    }
    return false;
  });
  const [currentLogoPreference, setCurrentLogoPreference] = useState(() => {
    try {
      const cachedLogoData = localStorage.getItem('sidebarLogoCache');
      if (cachedLogoData) {
        const { preference, isDark, hasCustomAssets } = JSON.parse(cachedLogoData);
        // Only use cached preference if dark mode matches AND user has custom assets
        if (isDark === darkMode && hasCustomAssets && preference) {
          return preference;
        }
      }
    } catch (e) {
      // If any error occurs reading cache, default to brandmark
    }
    return 'brandmark';
  });
  
  // Add loading state for logo to prevent flash of default logo
  const [isLogoLoading, setIsLogoLoading] = useState(() => {
    // Only show loading if we don't have ANY cached logo (custom or default)
    try {
      const cachedLogoData = localStorage.getItem('sidebarLogoCache');
      if (cachedLogoData) {
        const { isDark, logoUrl } = JSON.parse(cachedLogoData);
        // If we have any cached logo matching current theme, don't show loading
        if (isDark === darkMode && logoUrl) {
          return false;
        }
      }
    } catch (e) {
      // If any error occurs reading cache, show loading
    }
    return true;
  });
  
  // Projects state
  const [projects, setProjects] = useState([]);
  const [isProjectsCollapsed, setIsProjectsCollapsed] = useState(() => {
    // Get saved state from localStorage, default to expanded
    const saved = localStorage.getItem('sidebarProjectsCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [showAllProjects, setShowAllProjects] = useState(() => {
    // Get saved state from localStorage, default to false (show limited)
    const saved = localStorage.getItem('sidebarShowAllProjects');
    return saved ? JSON.parse(saved) : false;
  });
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  
  // Add typing state management for projects
  const [projectTypingUsers, setProjectTypingUsers] = useState({}); // projectId -> { userId: { userName, userColor, timestamp, profileImage } }
  const [projectRecentlyActive, setProjectRecentlyActive] = useState({}); // projectId -> { userId: { userName, userColor, profileImage, lastActiveTime } }
  
  // Separate state for project chat activity tracking
  const [projectChatActivity, setProjectChatActivity] = useState({}); // { projectId: { liveChats: number, activeUsers: [] } }
  
  // Clients state
  const [clients, setClients] = useState([]);
  const [isClientsCollapsed, setIsClientsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarClientsCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [showAllClients, setShowAllClients] = useState(() => {
    const saved = localStorage.getItem('sidebarShowAllClients');
    return saved ? JSON.parse(saved) : false;
  });
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [isCreateClientModalOpen, setIsCreateClientModalOpen] = useState(false);
  
  // Client view state - for showing a specific client's chats in sidebar
  const [viewingClient, setViewingClient] = useState(null);
  const [clientChats, setClientChats] = useState([]);
  const [isLoadingClientChats, setIsLoadingClientChats] = useState(false);
  
  // 🧪 Test Mode States
  const [isTestMode, setIsTestMode] = useState(false);
  const [testUserChats, setTestUserChats] = useState({});
  const [collapsedTestUsers, setCollapsedTestUsers] = useState(new Set());
  const [testUsersLoading, setTestUsersLoading] = useState(false);
  const [activeTestUser, setActiveTestUser] = useState(null); // Currently impersonated test user
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('🔊 Sidebar projectTypingUsers state changed:', projectTypingUsers);
  }, [projectTypingUsers]);
  
  useEffect(() => {
    console.log('🔊 Sidebar projectRecentlyActive state changed:', projectRecentlyActive);
  }, [projectRecentlyActive]);
  
  // 🧪 Test Mode Detection
  useEffect(() => {
    const checkTestMode = () => {
      const testOrgMode = localStorage.getItem('devTestOrgMode');
      const newIsTestMode = testOrgMode === 'true';
      
      if (newIsTestMode !== isTestMode) {
        console.log('🧪 Test mode changed:', newIsTestMode);
        setIsTestMode(newIsTestMode);
        
        // Reset states when switching modes
        if (newIsTestMode) {
          setTestUserChats({});
          setCollapsedTestUsers(new Set());
          setActiveTestUser(localStorage.getItem('devActiveTestUser')); // Restore active test user
          // Note: fetchTestUserChats will be called in the separate effect below
        } else {
          // Back to normal mode, clear test user and refetch regular chats
          setActiveTestUser(null);
          localStorage.removeItem('devActiveTestUser');
          if (fetchChatsRef.current) {
            fetchChatsRef.current(false, true);
          }
        }
      }
    };
    
    checkTestMode();
    
    // Listen for storage changes (when user switches orgs via console)
    const handleStorageChange = (e) => {
      if (e.key === 'devTestOrgMode') {
        checkTestMode();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isTestMode]);

  // Fetch brand assets on component mount and when darkMode changes (same as ChatMessage.js)
  useEffect(() => {
    const fetchBrandAssets = async (forcePreference = null) => {
      try {
        // Only show loading if we don't have any cached logo for this theme
        const cachedLogoData = localStorage.getItem('sidebarLogoCache');
        let shouldShowLoading = true;
        
        if (cachedLogoData) {
          try {
            const { isDark, logoUrl: cachedLogoUrl } = JSON.parse(cachedLogoData);
            // If we have a cached logo for this theme, don't show loading
            if (isDark === darkMode && cachedLogoUrl) {
              shouldShowLoading = false;
            }
          } catch (e) {
            // If error parsing cache, show loading
          }
        }
        
        if (shouldShowLoading) {
          setIsLogoLoading(true);
        }
        
        const token = localStorage.getItem('token');
        if (!token) {
          // No token - use default logo
          setLogoUrl(darkMode ? nvgtrBMDark : nvgtrBM);
          setLogoLoaded(true);
          setIsUsingCustomAssets(false);
          setCurrentLogoPreference('brandmark');
          setIsLogoLoading(false);
          return;
        }
        
        // Get brand assets - this checks both user and organization assets
        const { brandAssets, sidebarLogoPreference } = await getBrandAssetsCached({ token });
        
        // Only use brand assets if processed and enabled
        if (brandAssets?.isProcessed && brandAssets?.isEnabled !== false) {
          // Get the sidebar logo preference (use forced preference, then API preference, then cached preference, then default)
          const preference = forcePreference || sidebarLogoPreference || cachedPreference || 'brandmark';
          
          // Update cached preference if we got a new one from API
          if (!forcePreference && sidebarLogoPreference && sidebarLogoPreference !== cachedPreference) {
            setCachedPreference(sidebarLogoPreference);
          }
          
          // Update current preference state
          setCurrentLogoPreference(preference);
          
          let logo;
          if (preference === 'brandmark') {
            // Use brandMark for just the symbol
            logo = darkMode ? 
              brandAssets.brandMark?.white : 
              brandAssets.brandMark?.black;
          } else {
            // Use brandMarkWordMark for full logo (default)
            logo = darkMode ? 
              brandAssets.brandMarkWordMark?.white : 
              brandAssets.brandMarkWordMark?.black;
          }
          
          if (logo) {
            // Always use the proxy for S3 URLs
            const proxyUrl = getProxiedImageUrl(logo);
            setLogoUrl(proxyUrl);
            setLogoLoaded(true);
            setIsUsingCustomAssets(true);
            setIsLogoLoading(false);
            
            // Cache BOTH light and dark logo URLs so AccountModal/useFullLogo can switch
            // themes without falling back to default (same as Sidebar does)
            const logoLight = preference === 'brandmark'
              ? brandAssets.brandMark?.black
              : brandAssets.brandMarkWordMark?.black;
            const logoDark = preference === 'brandmark'
              ? brandAssets.brandMark?.white
              : brandAssets.brandMarkWordMark?.white;
            try {
              localStorage.setItem('sidebarLogoCache', JSON.stringify({
                logoUrl: proxyUrl,
                logoUrlLight: logoLight ? getProxiedImageUrl(logoLight) : null,
                logoUrlDark: logoDark ? getProxiedImageUrl(logoDark) : null,
                isDark: darkMode,
                preference: preference,
                hasCustomAssets: true,
                timestamp: Date.now()
              }));
              window.dispatchEvent(new CustomEvent('sidebar-logo-cache-updated'));
            } catch (e) {
              // Ignore storage errors
            }
            return;
          }
        }
        
        // Fall back to default logo if no brand assets or they're not usable
        setLogoUrl(darkMode ? nvgtrBMDark : nvgtrBM);
        setLogoLoaded(true);
        setIsUsingCustomAssets(false);
        setCurrentLogoPreference('brandmark'); // Default is always brandmark
        setIsLogoLoading(false);
        
        // Cache that no custom assets are available
        try {
          localStorage.setItem('sidebarLogoCache', JSON.stringify({
            logoUrl: darkMode ? nvgtrBMDark : nvgtrBM,
            isDark: darkMode,
            hasCustomAssets: false,
            timestamp: Date.now()
          }));
          window.dispatchEvent(new CustomEvent('sidebar-logo-cache-updated'));
        } catch (e) {
          // Ignore storage errors
        }
      } catch (error) {
        console.error('Error fetching brand assets:', error);
        // If there's an error, just use the default logo
        setLogoUrl(darkMode ? nvgtrBMDark : nvgtrBM);
        setLogoLoaded(true);
        setIsUsingCustomAssets(false);
        setCurrentLogoPreference('brandmark');
        setIsLogoLoading(false);
      }
    };
    
    fetchBrandAssets();
    
    // Listen for preference changes from the brand section
    const handlePreferenceChange = (event) => {
      console.log('Sidebar received preference change:', event.detail.preference);
      fetchBrandAssets(event.detail.preference);
    };
    
    window.addEventListener('sidebar-logo-preference-changed', handlePreferenceChange);
    
    return () => {
      window.removeEventListener('sidebar-logo-preference-changed', handlePreferenceChange);
    };
  }, [darkMode]);

  // Update default logo when dark mode changes (only if no custom logo is loaded)
  useEffect(() => {
    if (logoLoaded) {
      // Only update if we're still using the default logo (not custom brand assets)
      const currentLogo = darkMode ? nvgtrBMDark : nvgtrBM;
      if (logoUrl === nvgtrBM || logoUrl === nvgtrBMDark) {
        setLogoUrl(currentLogo);
      }
    } else {
      // Check if we have a cached logo for the new theme
      try {
        const cachedLogoData = localStorage.getItem('sidebarLogoCache');
        if (cachedLogoData) {
          const { isDark, logoUrl: cachedLogoUrl, hasCustomAssets } = JSON.parse(cachedLogoData);
          // If we have a cached logo for this theme, use it immediately
          if (isDark === darkMode && cachedLogoUrl) {
            setLogoUrl(cachedLogoUrl);
            setLogoLoaded(true);
            setIsLogoLoading(false);
            setIsUsingCustomAssets(hasCustomAssets || false);
          } else if (isDark !== darkMode && hasCustomAssets) {
            // Theme changed and we have custom assets - only show spinner if no logo for this theme
            setIsLogoLoading(true);
          }
        }
      } catch (e) {
        // If error reading cache, only show loading if we truly don't have a logo
        if (!logoUrl || logoUrl === '') {
          setIsLogoLoading(true);
        }
      }
    }
  }, [darkMode, logoLoaded, logoUrl]);
  
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [superBucketChats, setSuperBucketChats] = useState(new Set());
  const [activeMenu, setActiveMenu] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  
  
  // Animation state for new chats and promoted-to-top chats
  const [animatingChats, setAnimatingChats] = useState(new Set());
  const prevChatIds = useRef(new Set()); // Initialize with empty Set
  const clientChatsRef = useRef([]);
  const viewingClientRef = useRef(null);
  
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false
  });
  const lastFetchTime = useRef(0);
  const isFetchingRef = useRef(false); // Prevent simultaneous duplicate fetches
  const FETCH_COOLDOWN = 500;
  const LIMIT = 20;
  
  // Add ref to store fetchChats function
  const fetchChatsRef = useRef(null);
  
  // Add state for plus button hover menu
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const plusButtonRef = useRef(null);
  const [plusMenuPosition, setPlusMenuPosition] = useState({ x: 0, y: 0 });
  const plusMenuTimeoutRef = useRef(null);
  
  // Add state for project hover menu
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [projectMenuPosition, setProjectMenuPosition] = useState({ x: 0, y: 0 });
  const projectMenuTimeoutRef = useRef(null);
  
  // Add state for profile hover menu
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [profileMenuPosition, setProfileMenuPosition] = useState({ x: 0, y: 0 });
  const profileMenuTimeoutRef = useRef(null);
  const profileButtonRef = useRef(null);
  
  // Resizable sidebar
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Try to get saved width from localStorage, or use default width
    const savedWidth = localStorage.getItem('sidebarWidth');
    return savedWidth ? parseInt(savedWidth, 10) : 320;
  });
  const [isResizing, setIsResizing] = useState(false);
  const minWidth = 260;
  const maxWidth = 480;
  const sidebarRef = useRef(null);
  
  const startResizing = useCallback((mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    // Remove transition during resize for smoother experience
    if (sidebarRef.current) {
      sidebarRef.current.style.transition = 'none';
    }
  }, []);
  
  const stopResizing = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
    
    // Restore transition after resize is complete
    if (sidebarRef.current) {
      sidebarRef.current.style.transition = '';
    }
    
    // Save width to localStorage when resize operation completes
    localStorage.setItem('sidebarWidth', sidebarWidth.toString());
  }, [sidebarWidth]);
  
  const resize = useCallback((mouseMoveEvent) => {
    if (isResizing) {
      mouseMoveEvent.preventDefault();
      requestAnimationFrame(() => {
        const newWidth = Math.max(minWidth, Math.min(maxWidth, mouseMoveEvent.clientX));
        if (sidebarRef.current) {
          // Directly set width for smoother performance
          sidebarRef.current.style.width = `${newWidth}px`;
        }
        setSidebarWidth(newWidth);
      });
    }
  }, [isResizing, minWidth, maxWidth]);
  
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize, { passive: false });
      window.addEventListener('mouseup', stopResizing);
    }
    
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  const prefetchChat = useCallback(async (chatId) => {
    if (simpleCache.has(chatId)) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages?page=1`,
        { headers: getAuthHeaders() }
      );
      
      const messageData = Array.isArray(response.data) ? response.data : 
                         (response.data.messages || []);
      
      simpleCache.set(chatId, messageData);
    } catch (error) {
      console.error('Error prefetching chat:', error);
    }
  }, []);

  const debouncedPrefetch = useMemo(
    () => debounce((chatId) => {
      if (simpleCache.has(chatId)) return;
      prefetchChat(chatId);
    }, 500),
    [prefetchChat]
  );



  const sortedGroupedChats = useMemo(() => {
    // Filter out any null or undefined chat objects first
    const validChats = chats.filter(chat => chat && chat._id);
    
    const grouped = groupChatsByDate(validChats);
    // Sort chats within each group by date
    grouped.forEach(([_, dateChats]) => {
      dateChats.sort((a, b) => {
        const dateA = new Date(b.lastMessageAt || b.createdAt);
        const dateB = new Date(a.lastMessageAt || a.createdAt);
        return dateA - dateB;
      });
    });
    return grouped;
  }, [chats]);

  const fetchChats = useCallback(async (shouldAppend = false, force = false) => {
    try {
      const now = Date.now();
      
      // Prevent simultaneous duplicate fetches (race condition fix)
      if (isFetchingRef.current && !shouldAppend) {
        console.log('Skipping fetch - already fetching');
        return;
      }
      
      if (!shouldAppend && !force && now - lastFetchTime.current < FETCH_COOLDOWN) {
        console.log('Skipping fetch due to cooldown');
        return;
      }
      
      // Mark as fetching
      isFetchingRef.current = true;

      // Use cached data first if not appending and not forcing
      if (!shouldAppend && !force) {
        const cachedChats = simpleCache.get('all_chats');
        if (cachedChats && cachedChats.length > 0) {
          setChats(cachedChats);
          setIsLoading(false);
          isFetchingRef.current = false; // Clear flag before early return
          // Only fetch if cache is old
          if (now - lastFetchTime.current > 30000) { // 30 seconds
            updateCacheInBackground();
          }
          return;
        } else if (cachedChats) {
          console.log('Cache exists but is empty, forcing API call');
        } else {
          console.log('No cache found, making API call');
        }
      }

      // Get current values without depending on them in useCallback
      const currentChats = simpleCache.get('all_chats') || [];
      if (!shouldAppend && currentChats.length === 0) {
        setIsLoading(true);
      } else if (shouldAppend) {
        setIsPaginationLoading(true);
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setError('Authentication error');
        setIsLoading(false);
        isFetchingRef.current = false; // Clear flag before early return
        return;
      }

      const currentPage = shouldAppend ? page : 1;
      console.log(`Fetching chats - page: ${currentPage}, append: ${shouldAppend}`);
      
      // Fetch personal chats
      const personalChatsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat?page=${currentPage}&limit=${LIMIT}`,
        { headers: getAuthHeaders() }
      );

      // Remove project chat fetching from sidebar - personal sidebar should only show personal chats
      let projectChats = [];
      // Project chats are now handled in their respective project dashboards only
      // This ensures clean separation between personal and project chats
      
      console.log('📊 Sidebar fetching personal chats only - project chats handled separately');
      
      lastFetchTime.current = now;
      
      const { chats: personalChats, pagination } = personalChatsResponse.data;
      
      console.log(`Fetched ${personalChats.length} personal chats (project chats excluded from sidebar)`);
      
      // Only use personal chats for the sidebar
      const allNewChats = personalChats.filter(chat => !chat.projectId); // Extra safety filter
      
      setHasMore(pagination.hasMore);
      setPage(currentPage + 1);
      
      setChats(prevChats => {
        if (shouldAppend) {
          // For append, only add new personal chats (keep existing project chats)
          const existingIds = new Set(prevChats.filter(chat => chat && chat._id).map(chat => chat._id));
          const uniqueNewPersonalChats = personalChats.filter(chat => !chat || !chat._id || !existingIds.has(chat._id));
          const updatedChats = [...prevChats, ...uniqueNewPersonalChats];
          console.log(`Appended ${uniqueNewPersonalChats.length} new personal chats, total: ${updatedChats.length}`);
          simpleCache.set('all_chats', updatedChats);
          return updatedChats;
        }
        
        // Detect new chats for animation (only when not appending)
        if (!shouldAppend && prevChats.length > 0) {
          const currentPrevChatIds = new Set(prevChats.map(chat => chat._id));
          const newChatIds = new Set(allNewChats.map(chat => chat._id));
          
          // Find truly new chats (not in the previous set)
          const newlyAddedChats = allNewChats.filter(chat => 
            !currentPrevChatIds.has(chat._id) && 
            !prevChatIds.current.has(chat._id)
          );
          
          if (newlyAddedChats.length > 0) {
            console.log(`🎨 Animating ${newlyAddedChats.length} new chats:`, newlyAddedChats.map(c => c._id));
            
            // Mark new chats for animation
            const newAnimatingChats = new Set(newlyAddedChats.map(chat => chat._id));
            setAnimatingChats(newAnimatingChats);
            
            // Remove animation after animation completes
            setTimeout(() => {
              setAnimatingChats(new Set());
            }, 600); // Match animation duration
          }
          
          // Update previous chat IDs
          prevChatIds.current = newChatIds;
        } else if (!prevChatIds.current) {
          // Initialize prevChatIds on first load
          prevChatIds.current = new Set(allNewChats.map(chat => chat._id));
        }
        
        console.log(`Set ${allNewChats.length} total chats (${personalChats.length} personal + ${projectChats.length} project)`);
        simpleCache.set('all_chats', allNewChats);
        return allNewChats;
      });

      // Extract superbucket chats directly from the API response
      const newSuperBucketChats = new Set();
      allNewChats.forEach(chat => {
        if (chat.isSuperBucketChat) {
          newSuperBucketChats.add(chat._id);
        }
      });
      setSuperBucketChats(newSuperBucketChats);

      // Prefetch first few chats
      allNewChats.slice(0, 3).forEach(chat => {
        debouncedPrefetch(chat._id);
      });
      
      setError(null);
    } catch (error) {
      console.error('Error fetching chats:', error);
      setError('Failed to fetch chats');
    } finally {
      setIsLoading(false);
      setIsPaginationLoading(false);
      isFetchingRef.current = false; // Clear fetch lock
    }
  }, [page, debouncedPrefetch, user?.companyId, projects]); // Removed chats.length dependency

  // Store fetchChats in ref for use in useEffects
  fetchChatsRef.current = fetchChats;

  // Function to fetch project chat activity data
  const fetchProjectChatActivity = useCallback(async () => {
    if (!user?.companyId || !projects.length) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const activityData = {};
      
      // Filter out mock/test projects to avoid 404 errors
      const realProjects = projects.filter(project => {
        // Skip projects with test IDs that start with '64c8f5e8d1234567890abcd'
        return !project.id.startsWith('64c8f5e8d1234567890abcd');
      });
      
      // Fetch activity for each real project
      await Promise.all(realProjects.map(async (project) => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/projects/${project.id}/chats`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const projectChats = response.data.chats || [];
          const liveChats = projectChats.filter(chat => 
            chat.activeUsers && chat.activeUsers.length > 0
          );
          
          activityData[project.id] = {
            liveChats: liveChats.length,
            activeUsers: liveChats.reduce((acc, chat) => {
              return acc.concat(chat.activeUsers || []);
            }, [])
          };
          
        } catch (error) {
          console.error(`Error fetching activity for project ${project.id}:`, error);
          activityData[project.id] = { liveChats: 0, activeUsers: [] };
        }
      }));
      
      // Set default activity for mock projects to avoid missing data
      projects.forEach(project => {
        if (project.id.startsWith('64c8f5e8d1234567890abcd')) {
          activityData[project.id] = { liveChats: 0, activeUsers: [] };
        }
      });
      
      setProjectChatActivity(activityData);
      console.log('🔴 Updated project chat activity:', activityData);
      
    } catch (error) {
      console.error('Error fetching project chat activity:', error);
    }
  }, [user?.companyId, projects]);

  // 🧪 Fetch Test User Chats
  const fetchTestUserChats = useCallback(async () => {
    if (!isTestMode) return;
    
    setTestUsersLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // 🛠️ Prepare headers with test org override
      const headers = {
        Authorization: `Bearer ${token}`,
        'X-Test-Org-Id': localStorage.getItem('devTestOrgId')
      };
      
      console.log('🧪 Fetching test user chats...');
      
      // Fetch chats for test org - we'll get all chats and group them by user
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat?page=1&limit=100`, // Get more chats for test exploration
        { headers }
      );
      
      const { chats } = response.data;
      console.log('🧪 Received test chats:', chats.length);
      
      // Group chats by user (simulating different test users)
      const testUsers = {
        'Test Admin': {
          role: 'Owner',
          chats: chats.filter((_, index) => index % 7 === 0),
          color: '#DC2626', // Red
          userId: '686a18977fb2313c0101e362', // Real test admin user ID from script
          isAdmin: true
        },
        'Mike Chen': { 
          role: 'Marketing Manager', 
          chats: chats.filter((_, index) => index % 7 === 1),
          color: '#3B82F6' // Blue
        },
        'Lisa Rodriguez': { 
          role: 'Senior Developer', 
          chats: chats.filter((_, index) => index % 7 === 2),
          color: '#10B981' // Green
        },
        'David Kim': { 
          role: 'Sales Director', 
          chats: chats.filter((_, index) => index % 7 === 3),
          color: '#F59E0B' // Yellow
        },
        'Rachel Wang': { 
          role: 'HR Manager', 
          chats: chats.filter((_, index) => index % 7 === 4),
          color: '#EF4444' // Red
        },
        'Emily Foster': { 
          role: 'Product Manager', 
          chats: chats.filter((_, index) => index % 7 === 5),
          color: '#8B5CF6' // Purple
        },
        'James Thompson': { 
          role: 'CFO', 
          chats: chats.filter((_, index) => index % 7 === 6),
          color: '#06B6D4' // Cyan
        }
      };
      
      // Add mock titles to make chats more realistic for each user
      Object.keys(testUsers).forEach(userName => {
        testUsers[userName].chats = testUsers[userName].chats.map((chat, index) => {
          const mockTitles = {
            'Test Admin': [
              'Organization Overview',
              'SuperBucket Analytics Review', 
              'Team Performance Analysis',
              'Strategic Planning Session',
              'Budget Approval Meeting',
              'Policy Review Discussion'
            ],
            'Mike Chen': [
              'Marketing Campaign Planning',
              'Legal Approval Process Discussion',
              'Brand Strategy Review',
              'Campaign Performance Analysis',
              'Social Media Strategy',
              'Content Calendar Planning'
            ],
            'Lisa Rodriguez': [
              'Code Review Process Issues',
              'API Performance Optimization',
              'Database Migration Planning',
              'Bug Triage Meeting',
              'Tech Stack Upgrade Discussion',
              'Deployment Pipeline Review'
            ],
            'David Kim': [
              'Sales Proposal Process',
              'Client Meeting Preparation',
              'Competitive Analysis',
              'Q4 Sales Strategy',
              'Pipeline Review',
              'Customer Feedback Analysis'
            ],
            'Rachel Wang': [
              'New Hire Onboarding Issues',
              'IT Access Process Review',
              'Employee Feedback Session',
              'Benefits Enrollment Help',
              'Team Building Planning',
              'Performance Review Prep'
            ],
            'Emily Foster': [
              'Product Feature Planning',
              'Cross-team Coordination',
              'User Research Findings',
              'Product Roadmap Review',
              'Feature Launch Planning',
              'Customer Journey Mapping'
            ],
            'James Thompson': [
              'Q4 Budget Planning',
              'Cash Flow Analysis',
              'Financial Reporting',
              'Investment Planning',
              'Cost Optimization Review',
              'Revenue Projections'
            ]
          };
          
          return {
            ...chat,
            title: mockTitles[userName][index % mockTitles[userName].length],
            _testUser: userName
          };
        });
      });
      
      setTestUserChats(testUsers);
      console.log('🧪 Test user chats organized:', Object.keys(testUsers).map(user => 
        `${user}: ${testUsers[user].chats.length} chats`
      ));
      
    } catch (error) {
      console.error('🧪 Error fetching test user chats:', error);
    } finally {
      setTestUsersLoading(false);
    }
  }, [isTestMode]);

  // 🧪 Fetch test chats when test mode is active
  useEffect(() => {
    if (isTestMode) {
      console.log('🧪 Test mode is active, fetching test user chats...');
      fetchTestUserChats();
    }
  }, [isTestMode, fetchTestUserChats]);

  // 🧪 Toggle test user section collapse
  const toggleTestUserCollapse = (userName) => {
    setCollapsedTestUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userName)) {
        newSet.delete(userName);
      } else {
        newSet.add(userName);
      }
      return newSet;
    });
  };

  // 🧪 Switch to test user (impersonation)
  const switchToTestUser = (userName) => {
    console.log('🧪 Switching to test user:', userName);
    
    // Use real test user IDs
    let userId;
    if (userName === 'Test Admin') {
      userId = '686a18977fb2313c0101e362'; // Real test admin user ID from script
    } else {
      userId = `test-user-${btoa(userName).slice(0, 24)}`; // Create consistent ID from name
    }
    
    setActiveTestUser(userName);
    
    // Store in localStorage so other components can use it
    localStorage.setItem('devActiveTestUser', userName);
    localStorage.setItem('devActiveTestUserId', userId);
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('testUserChanged', { 
      detail: { 
        userName, 
        userId: userId,
        userData: testUserChats[userName] 
      } 
    }));
    
    console.log('🧪 Now impersonating:', userName, 'with ID:', userId);
  };

  // 🧪 Clear test user impersonation
  const clearTestUser = () => {
    console.log('🧪 Clearing test user impersonation');
    setActiveTestUser(null);
    localStorage.removeItem('devActiveTestUser');
    localStorage.removeItem('devActiveTestUserId');
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('testUserChanged', { 
      detail: { 
        userName: null, 
        userId: null,
        userData: null 
      } 
    }));
  };

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log('🎯 Sidebar received WebSocket message:', data);
    
    if (data.type === 'chat_update') {
      const updateType = data.data?.type;
      
      // Handle streaming complete - chat moved to top (real-time re-sort + animation)
      if (updateType === 'streaming_complete' && data.chatId) {
        const chatId = data.chatId;
        const now = new Date();
        const wasAlreadyAtTopRef = { current: false };
        // Update main chats list if chat exists there - re-sort so promoted chat goes first
        setChats(prevChats => {
          const chatIndex = prevChats.findIndex(c => c && c._id === chatId);
          const chat = chatIndex >= 0 ? prevChats[chatIndex] : null;
          if (!chat) return prevChats;
          wasAlreadyAtTopRef.current = (chatIndex === 0);
          const updatedChats = prevChats.map(c => 
            c && c._id === chatId ? { ...c, lastMessageAt: now } : c
          );
          updatedChats.sort((a, b) => 
            new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
          );
          simpleCache.set('all_chats', updatedChats);
          return updatedChats;
        });
        // Update client chats if viewing a client and chat is in that list - re-sort
        if (viewingClientRef.current && clientChatsRef.current.some(c => c && c._id === chatId)) {
          setClientChats(prev => {
            const updated = prev.map(c => 
              c && c._id === chatId ? { ...c, lastMessageAt: now } : c
            );
            updated.sort((a, b) => 
              new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
            );
            return updated;
          });
        }
        // Only animate if the chat actually moved to the top (wasn't already there)
        // Defer so setChats callback runs first and wasAlreadyAtTopRef is set
        setTimeout(() => {
          if (!wasAlreadyAtTopRef.current) {
            setAnimatingChats(prev => new Set([...prev, chatId]));
            setTimeout(() => {
              setAnimatingChats(prev => {
                const next = new Set(prev);
                next.delete(chatId);
                return next;
              });
            }, 600);
          }
        }, 0);
      }
      
      // Handle user joining/leaving chats - INSTANT red dot updates
      if (updateType === 'user_joined_chat' || updateType === 'user_left_chat') {
        console.log(`🔴 INSTANT sidebar update: ${updateType} in chat ${data.chatId}`);
        
        // IMMEDIATE chat list refresh for personal chats
        fetchChats();
        
        // IMMEDIATE project activity refresh for red dots
        fetchProjectChatActivity();
        
        // Update red dots immediately for personal chats
        setChats(prevChats => {
          return prevChats.map(chat => {
            if (chat._id === data.chatId) {
              const updatedActiveUsers = data.data?.activeUsers || [];
              return {
                ...chat,
                activeUsers: updatedActiveUsers
              };
            }
            return chat;
          });
        });
      }
    }

    // Handle project chat updates
    if (data.type === 'projectChatUpdate') {
      console.log('🔄 Project chat update received in sidebar');
      // Refresh project activity for updated red dots
      fetchProjectChatActivity();
    }

    // REMOVED: WebSocket handler for newChatCreated
    // The window event listener (line ~1392) already handles this
    // Having both caused duplicate GET_CHATS calls
  }, [fetchChats, fetchProjectChatActivity]);

  // Use the new WebSocket manager - only connect when user is fully loaded
  const { isConnected } = useWebSocketManager('sidebar-main', {
    path: '/api/ws',
    onMessage: handleWebSocketMessage,
    autoConnect: !!(user?.id && user?.isOnboarded),
    autoReconnect: true
  });

  // Keep refs updated for WebSocket handler (client view)
  useEffect(() => {
    clientChatsRef.current = clientChats;
    viewingClientRef.current = viewingClient;
  }, [clientChats, viewingClient]);

  // Initial project activity fetch
  useEffect(() => {
    if (projects.length > 0) {
      fetchProjectChatActivity();
    }
  }, [projects, fetchProjectChatActivity]);

  // Listen for custom project chat update events
  useEffect(() => {
    const handleProjectChatUpdate = () => {
      console.log('🔄 Custom project chat update event received');
      fetchProjectChatActivity();
    };

    window.addEventListener('projectChatUpdate', handleProjectChatUpdate);
    
    return () => {
      window.removeEventListener('projectChatUpdate', handleProjectChatUpdate);
    };
  }, [fetchProjectChatActivity]);

  // Listen for new chat created events (dispatched from Chat.js)
  useEffect(() => {
    const handleNewChatCreated = async (event) => {
      console.log('🆕 New chat created event received, refreshing sidebar');
      fetchChats(false, true); // Force fresh fetch, don't use cache
      
      // Also refresh client chats if viewing a client
      if (viewingClient) {
        try {
          const token = localStorage.getItem('token');
          const clientId = viewingClient.id || viewingClient._id;
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/clients/${clientId}/chats`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setClientChats(response.data.chats || []);
        } catch (error) {
          console.error('Error refreshing client chats:', error);
        }
      }
    };

    window.addEventListener('newChatCreated', handleNewChatCreated);
    
    return () => {
      window.removeEventListener('newChatCreated', handleNewChatCreated);
    };
  }, [fetchChats, viewingClient]);

  // Listen for chat title updates (auto-generated titles from streaming)
  useEffect(() => {
    const handleChatTitleUpdated = (event) => {
      // Handle both 'title' and 'newTitle' formats
      const { chatId, title, newTitle } = event.detail;
      const updatedTitle = title || newTitle;
      
      if (!chatId || !updatedTitle) return;
      
      // Update in main chats list
      setChats(prevChats => 
        prevChats.map(chat => 
          chat._id === chatId ? { ...chat, title: updatedTitle } : chat
        )
      );
      
      // Also update in client chats if viewing a client
      if (viewingClient) {
        setClientChats(prevChats => 
          prevChats.map(chat => 
            chat._id === chatId ? { ...chat, title: updatedTitle } : chat
          )
        );
      }
    };

    window.addEventListener('chatTitleUpdated', handleChatTitleUpdated);
    
    return () => {
      window.removeEventListener('chatTitleUpdated', handleChatTitleUpdated);
    };
  }, [viewingClient]);

  // Cleanup stale typing indicators for projects
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 60000; // 1 minute timeout

      setProjectTypingUsers(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(projectId => {
          const projectTypingUsers = { ...updated[projectId] };
          
          Object.keys(projectTypingUsers).forEach(userId => {
            if (now - projectTypingUsers[userId].timestamp > staleThreshold) {
              delete projectTypingUsers[userId];
              hasChanges = true;
            }
          });

          if (Object.keys(projectTypingUsers).length === 0) {
            delete updated[projectId];
            hasChanges = true;
          } else {
            updated[projectId] = projectTypingUsers;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 5000); // Check every 5 seconds

    return () => clearInterval(cleanupInterval);
  }, []);

  // Watch for location changes to ensure proper highlighting
  // REMOVED: fetchChats call here - the newChatCreated event listener already handles new chats
  // Having both caused duplicate GET_CHATS calls when creating a new chat
  useEffect(() => {
    // Just update highlighting based on URL, don't fetch
    // The newChatCreated event listener handles fetching for new chats
    // For edge cases (bookmarked chat not in list), user can refresh manually
  }, [location.pathname]);

  // Initial chat fetch
  useEffect(() => {
    if (fetchChatsRef.current) {
      fetchChatsRef.current(false);
    }
  }, []); // Remove fetchChats dependency - only run once on mount

  // Update the useEffect for intersection observer
  useEffect(() => {
    if (inView && !isPaginationLoading && hasMore && fetchChatsRef.current) {
      fetchChatsRef.current(true);
    }
  }, [inView, isPaginationLoading, hasMore]); // Remove fetchChats dependency

  // Throttle WebSocket updates
  const handleWebSocketUpdate = useCallback(
    throttle((updatedChat) => {
      if (!updatedChat || !updatedChat._id) {
        console.warn('Attempted update with invalid chat:', updatedChat);
        return;
      }
      
      setChats(prevChats => {
        // Only update if the chat actually changed
        const existingChat = prevChats.find(c => c && c._id === updatedChat._id);
        if (existingChat && JSON.stringify(existingChat) === JSON.stringify(updatedChat)) {
          return prevChats;
        }
        return prevChats.map(chat => 
          chat && chat._id === updatedChat._id ? updatedChat : chat
        );
      });
    }, 1000, { leading: true, trailing: true }),
    []
  );

  const filteredChats = useMemo(() => {
    return chats.filter(chat => chat && chat._id);
  }, [chats]);

  const formatChatTitle = (chat) => {
    if (chat.title) return chat.title;
    if (chat.messages && chat.messages[0]) {
      return chat.messages[0].content;
    }
    return 'New Chat';
  };

  // Get client color for a chat
  const getClientColorForChat = (chat) => {
    if (!chat.clientId) return null;
    const client = clients.find(c => (c._id || c.id) === chat.clientId);
    return client?.color || '#6366f1';
  };

  const handleMenuClick = (e, chatId) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMenu(chatId);
    setMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleRename = (e, chatId, currentTitle) => {
    e.stopPropagation();
    setEditingChatId(chatId);
    setEditValue(currentTitle);
    setActiveMenu(null);
  };

  const handleRenameSubmit = async (chatId) => {
    if (!editValue.trim()) {
      setEditingChatId(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Immediately update local state for better UX
      setChats(prevChats => {
        return prevChats.map(chat => 
          chat._id === chatId ? { ...chat, title: editValue } : chat
        );
      });
      
      // Also update clientChats if viewing a client
      if (viewingClient) {
        setClientChats(prevChats => {
          return prevChats.map(chat => 
            chat._id === chatId ? { ...chat, title: editValue } : chat
          );
        });
      }
      
      // Clear editing state
      setEditingChatId(null);
      
      // Dispatch event so header updates
      window.dispatchEvent(new CustomEvent('chatTitleUpdated', {
        detail: { chatId, title: editValue }
      }));
      
      // Make the API call
      await axios.put(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/title`,
        { title: editValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error renaming chat:', error);
      setError('Failed to rename chat');
      // Don't reset editing state on error to allow retry
    }
  };

  const handleDelete = (e, chatId) => {
    e.preventDefault(); // Prevent any default actions
    e.stopPropagation();
    setChatToDelete(chatId);
    setDeleteModalOpen(true);
    setActiveMenu(null);
  };

  const confirmDelete = async () => {
    if (!chatToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const deletingChatId = chatToDelete;
      
      // First close modal and update UI to appear responsive
      setDeleteModalOpen(false);
      
      // Update local state immediately to remove the deleted chat
      setChats(prevChats => {
        // Create a new array without the deleted chat
        const newChats = prevChats.filter(chat => chat && chat._id !== deletingChatId);
        // Also update the cache
        simpleCache.set('all_chats', newChats);
        return newChats;
      });
      
      // Also update clientChats if viewing a client
      if (viewingClient) {
        setClientChats(prevChats => {
          return prevChats.filter(chat => chat && chat._id !== deletingChatId);
        });
      }
      
      // Also remove this chat's messages from cache
      simpleCache.delete(deletingChatId);
      
      // If we're deleting the currently active chat, navigate away
      if (activeChat === deletingChatId) {
        onChatSelect(null);
      }
      
      // Reset other state
      setChatToDelete(null);
      setActiveMenu(null);
      
      // Then perform the actual API call
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/chat/${deletingChatId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error deleting chat:", error);
      setError("Failed to delete chat");
      // Refresh chat list to ensure UI is correct
      fetchChats();
    }
  };

  useImperativeHandle(ref, () => ({
    fetchChats,
    getChats: () => chats,
    sortedGroupedChats
  }));

  const updateCacheInBackground = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch personal chats
      const personalChatsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const personalChats = Array.isArray(personalChatsResponse.data) ? personalChatsResponse.data : 
                      (personalChatsResponse.data.chats || []);

      // Remove project chat fetching from background update - sidebar only handles personal chats
      let projectChats = [];
      console.log('🔄 Background update: personal chats only');

      // Only personal chats for sidebar
      const allChats = personalChats.filter(chat => !chat.projectId); // Extra safety filter

      setChats(prevChats => {
        // Only update if there are actual changes
        if (JSON.stringify(prevChats) !== JSON.stringify(allChats)) {
          simpleCache.set('all_chats', allChats);
          return allChats;
        }
        return prevChats;
      });
    } catch (error) {
      console.error('Error updating cache:', error);
    }
  }, [user?.companyId, projects]);

  // Add a function to check if a chat is active
  const isChatActive = (chatId) => {
    return location.pathname === `/chat/${chatId}`;
  };

  // Check if a project has live chats (chats with active users)
  const projectHasLiveChats = useCallback((projectId) => {
    if (projectId === 'personal') {
      // For personal chats, check chats without projectId
      const personalLiveChats = chats.filter(chat => 
        chat && 
        !chat.projectId && 
        chat.activeUsers && 
        chat.activeUsers.length > 0
      );
      if (personalLiveChats.length > 0) {
        console.log(`🔴 Personal has ${personalLiveChats.length} live chats`);
      }
      return personalLiveChats.length > 0;
    } else {
      // For project chats, use the separate project activity tracking
      const projectActivity = projectChatActivity[projectId];
      const hasLiveChats = projectActivity && projectActivity.liveChats > 0;
      
      if (hasLiveChats) {
        console.log(`🔴 Project ${projectId} has ${projectActivity.liveChats} live chats`);
      }
      return hasLiveChats;
    }
  }, [chats, projectChatActivity]);

  // Check if a project is currently active (either viewing project dashboard or project chat)
  const isProjectActive = useCallback((projectId) => {
    if (location.pathname === '/') {
      return projectId === 'personal';
    }
    
    // Handle project greeting page (/chat with ?project=projectId)
    if (location.pathname === '/chat') {
      const searchParams = new URLSearchParams(location.search);
      const urlProjectId = searchParams.get('project');
      
      if (urlProjectId) {
        return projectId === urlProjectId;
      }
      
      // If no project param on greeting page, it's personal
      return projectId === 'personal';
    }
    
    if (location.pathname.startsWith('/chat/')) {
      const searchParams = new URLSearchParams(location.search);
      const urlProjectId = searchParams.get('project');
      
      if (urlProjectId) {
        return projectId === urlProjectId;
      }
      
      // If no project param, check if this chat belongs to the project
      const chatId = location.pathname.split('/')[2];
      const chat = chats.find(c => c._id === chatId);
      
      if (chat?.projectId === projectId) {
        return true;
      }
    }
    
    if (location.pathname.startsWith('/project/')) {
      const urlProjectId = location.pathname.split('/')[2];
      return projectId === urlProjectId;
    }
    
    return false;
  }, [location.pathname, location.search, chats]);

  // Sort projects by last interaction time (most recent first)
  const sortedProjects = useMemo(() => {
    if (!projects.length) return [];
    
    return [...projects].sort((a, b) => {
      // Find the most recent chat activity for each project
      const getLastActivityTime = (projectId) => {
        const projectChats = chats.filter(chat => chat.projectId === projectId);
        if (!projectChats.length) return 0; // Projects with no chats go to the end
        
        // Find the most recent activity (lastMessageAt or createdAt)
        const mostRecentTime = Math.max(
          ...projectChats.map(chat => {
            const lastMessageAt = chat.lastMessageAt ? new Date(chat.lastMessageAt).getTime() : 0;
            const createdAt = chat.createdAt ? new Date(chat.createdAt).getTime() : 0;
            return Math.max(lastMessageAt, createdAt);
          })
        );
        
        return mostRecentTime;
      };
      
      const aTime = getLastActivityTime(a.id);
      const bTime = getLastActivityTime(b.id);
      
      // Sort by most recent first, projects with no activity go to the end
      return bTime - aTime;
    });
  }, [projects, chats]);

  const { handleImageError } = useS3Image(user, setUser);

  // Function to determine logo className based on logo type
  const getLogoClassName = () => {
    // Default NVGTR logos always use w-8 h-8
    if (!isUsingCustomAssets) {
      return "h-8 w-8 max-[1200px]:h-7 max-[1200px]:w-7 max-[900px]:h-6 max-[900px]:w-6 [@media(max-height:800px)]:h-7 [@media(max-height:800px)]:w-7 [@media(max-height:700px)]:h-6 [@media(max-height:700px)]:w-6";
    }
    
    // Custom assets: brandmark uses w-8 h-8, full logo uses w-auto h-8
    if (currentLogoPreference === 'brandmark') {
      return "h-8 w-8 max-[1200px]:h-7 max-[1200px]:w-7 max-[900px]:h-6 max-[900px]:w-6 [@media(max-height:800px)]:h-7 [@media(max-height:800px)]:w-7 [@media(max-height:700px)]:h-6 [@media(max-height:700px)]:w-6";
    } else {
      return "p-0.5 h-6 w-auto max-[1200px]:h-6 max-[1200px]:p-0.5 max-[900px]:h-5 max-[900px]:p-0 [@media(max-height:800px)]:h-6 [@media(max-height:800px)]:p-0.5 [@media(max-height:700px)]:h-5 [@media(max-height:700px)]:p-0";
    }
  };

  // Plus menu handlers
  const handlePlusMenuEnter = () => {
    // Clear any existing timeout
    if (plusMenuTimeoutRef.current) {
      clearTimeout(plusMenuTimeoutRef.current);
      plusMenuTimeoutRef.current = null;
    }
    
    if (plusButtonRef.current) {
      const rect = plusButtonRef.current.getBoundingClientRect();
      setPlusMenuPosition({
        x: sidebarWidth + 10, // Position to the right of the sidebar with 4px gap
        y: rect.top
      });
    }
    setIsPlusMenuOpen(true);
  };

  const handlePlusMenuLeave = () => {
    // Add a small delay before closing to allow mouse to move to menu
    plusMenuTimeoutRef.current = setTimeout(() => {
      setIsPlusMenuOpen(false);
    }, 150);
  };

  const handleNewChat = () => {
    setIsPlusMenuOpen(false);
    navigate('/chat');
  };

  const handleNewProject = () => {
    setIsPlusMenuOpen(false);
    setIsCreateProjectModalOpen(true);
  };

  const handleNewClient = () => {
    setIsPlusMenuOpen(false);
    setIsCreateClientModalOpen(true);
  };

  // Project menu handlers
  const handleProjectMenuEnter = (project, event) => {
    // Clear any existing timeout
    if (projectMenuTimeoutRef.current) {
      clearTimeout(projectMenuTimeoutRef.current);
      projectMenuTimeoutRef.current = null;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setProjectMenuPosition({
      x: sidebarWidth + 10, // Position to the right of the sidebar with 10px gap
      y: rect.top
    });
    
    setHoveredProject(project);
    setIsProjectMenuOpen(true);
  };

  const handleProjectMenuLeave = () => {
    // Add a small delay before closing to allow mouse to move to menu
    projectMenuTimeoutRef.current = setTimeout(() => {
      setIsProjectMenuOpen(false);
      setHoveredProject(null);
    }, 150);
  };

  const handleGoToProject = (projectId) => {
    setIsProjectMenuOpen(false);
    setHoveredProject(null);
    navigate(`/project/${projectId}`);
  };

  // Profile menu handlers
  const handleProfileMenuEnter = () => {
    // Don't show menu if AccountModal is open
    if (isAccountModalOpen) return;
    
    // Clear any existing timeout
    if (profileMenuTimeoutRef.current) {
      clearTimeout(profileMenuTimeoutRef.current);
      profileMenuTimeoutRef.current = null;
    }
    
    if (profileButtonRef.current) {
      const rect = profileButtonRef.current.getBoundingClientRect();
      setProfileMenuPosition({
        x: sidebarWidth + 10, // Position to the right of the sidebar with 10px gap
        y: rect.top - 160 // Position just above the profile button
      });
    }
    setIsProfileMenuOpen(true);
  };

  const handleProfileMenuLeave = () => {
    // Add a small delay before closing to allow mouse to move to menu
    profileMenuTimeoutRef.current = setTimeout(() => {
      setIsProfileMenuOpen(false);
    }, 150);
  };

  const handleProfileMenuClick = (section) => {
    setIsProfileMenuOpen(false);
    onChatSelect(null);
    navigate(`/profile?section=${section}`);
  };

  // Projects functionality
  const fetchProjects = useCallback(async () => {
    if (!user?.companyId) {
      console.log('No companyId found for user:', user);
      return;
    }
    
    setIsLoadingProjects(true);
    setProjectsError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      console.log('Fetching projects for companyId:', user.companyId);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Projects response:', response.data);
      let fetchedProjects = response.data.projects || [];
      
      // Merge with test data using the organized system
      fetchedProjects = mergeWithTestData(fetchedProjects, getTestProjects);
      
      // Log test configuration for debugging
      logTestConfig();
      
      setProjects(fetchedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to load projects';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setProjectsError(errorMessage);
      setProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [user?.companyId]);

  // Fetch projects when component mounts and when user changes
  useEffect(() => {
    if (user?.companyId) {
      fetchProjects();
    }
  }, [fetchProjects, user?.companyId]);

  // Refetch chats when projects are loaded to include project chats
  useEffect(() => {
    if (projects.length > 0 && user?.companyId && fetchChatsRef.current) {
      console.log('🔄 Projects loaded, refetching chats to include project chats');
      // Force a refresh to include project chats
      fetchChatsRef.current(false, true);
    }
  }, [projects.length, user?.companyId]); // Remove fetchChats dependency

  // Listen for projects update event (e.g., when user accepts invitation)
  useEffect(() => {
    const handleProjectsUpdated = () => {
      console.log('🔄 Sidebar received projectsUpdated event - refreshing projects');
      if (user?.companyId) {
        fetchProjects();
      }
    };

    window.addEventListener('projectsUpdated', handleProjectsUpdated);
    return () => {
      window.removeEventListener('projectsUpdated', handleProjectsUpdated);
    };
  }, [fetchProjects, user?.companyId]);

  // Handle project creation
  const handleProjectCreated = (newProject) => {
    setProjects(prev => [newProject, ...prev]);
    // Optionally navigate to the project or show success message
  };

  // ============================================================================
  // CLIENTS FUNCTIONALITY
  // ============================================================================
  
  // Fetch clients for the current user
  const fetchClients = useCallback(async () => {
    if (!user?.companyId) {
      return;
    }
    
    setIsLoadingClients(true);
    setClientsError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/clients`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      
      let errorMessage = 'Failed to load clients';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setClientsError(errorMessage);
      setClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  }, [user?.companyId]);

  // Fetch clients when component mounts
  useEffect(() => {
    if (user?.companyId) {
      fetchClients();
    }
  }, [fetchClients, user?.companyId]);

  // Listen for clients update event
  useEffect(() => {
    const handleClientsUpdated = () => {
      if (user?.companyId) {
        fetchClients();
      }
    };

    window.addEventListener('clientsUpdated', handleClientsUpdated);
    return () => {
      window.removeEventListener('clientsUpdated', handleClientsUpdated);
    };
  }, [fetchClients, user?.companyId]);

  // Fetch chats for a specific client
  const fetchClientChats = useCallback(async (clientId) => {
    setIsLoadingClientChats(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/clients/${clientId}/chats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClientChats(response.data.chats || []);
    } catch (error) {
      console.error('Error fetching client chats:', error);
      setClientChats([]);
    } finally {
      setIsLoadingClientChats(false);
    }
  }, []);

  // Handle clicking on a client - show their chats in sidebar and navigate to dashboard
  const handleClientClick = useCallback((client) => {
    setViewingClient(client);
    fetchClientChats(client.id || client._id);
    // Navigate to client dashboard
    navigate(`/client/${client.id || client._id}`);
  }, [fetchClientChats, navigate]);

  // Go back to main sidebar view
  const handleBackFromClientView = useCallback(() => {
    setViewingClient(null);
    setClientChats([]);
  }, []);

  // Handle client creation
  const handleClientCreated = (newClient) => {
    setClients(prev => [newClient, ...prev]);
    // Open the client view in sidebar
    handleClientClick(newClient);
  };

  // Toggle clients section collapse
  const toggleClientsCollapse = () => {
    const newState = !isClientsCollapsed;
    setIsClientsCollapsed(newState);
    localStorage.setItem('sidebarClientsCollapsed', JSON.stringify(newState));
  };

  // Toggle show all clients
  const toggleShowAllClients = () => {
    const newState = !showAllClients;
    setShowAllClients(newState);
    localStorage.setItem('sidebarShowAllClients', JSON.stringify(newState));
  };

  // Sort clients by last activity
  const sortedClients = useMemo(() => {
    if (!clients.length) return [];
    
    return [...clients].sort((a, b) => {
      const aTime = a.metadata?.lastActivity ? new Date(a.metadata.lastActivity).getTime() : 0;
      const bTime = b.metadata?.lastActivity ? new Date(b.metadata.lastActivity).getTime() : 0;
      return bTime - aTime;
    });
  }, [clients]);

  // Check if a client is currently active/selected
  const isClientActive = (clientId) => {
    const currentClientId = viewingClient?.id || viewingClient?._id;
    return currentClientId === clientId || location.pathname === `/client/${clientId}`;
  };

  // Check for saved CreateProjectModal state on mount
  useEffect(() => {
    const savedModalState = localStorage.getItem('createProjectModal');
    console.log('Checking for saved modal state:', savedModalState);
    
    if (savedModalState) {
      try {
        const parsedState = JSON.parse(savedModalState);
        console.log('Parsed modal state:', parsedState);
        
        if (parsedState.isOpen) {
          console.log('Auto-opening modal from saved state');
          setIsCreateProjectModalOpen(true);
        }
      } catch (error) {
        console.error('Error parsing saved modal state:', error);
        localStorage.removeItem('createProjectModal');
      }
    }
  }, []);

  // Toggle projects section collapse
  const toggleProjectsCollapse = () => {
    const newState = !isProjectsCollapsed;
    setIsProjectsCollapsed(newState);
    // Save state to localStorage
    localStorage.setItem('sidebarProjectsCollapsed', JSON.stringify(newState));
  };

  // Toggle show all projects
  const toggleShowAllProjects = () => {
    const newState = !showAllProjects;
    setShowAllProjects(newState);
    // Save state to localStorage
    localStorage.setItem('sidebarShowAllProjects', JSON.stringify(newState));
  };

  // Update the projects rendering section to use the new avatar stack
  const renderProjectsSection = () => {
    return (
      <div className="pb-3">
        {/* Projects Header */}
        <button 
          onClick={toggleProjectsCollapse}
          className="flex items-center justify-between pl-3 pr-2 py-2 w-full text-left"
        >
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <LuFolders className="w-4 h-4" />
            <span className="text-[13px] font-medium" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Projects
            </span>
          </div>
          
          <div className="flex items-center">
            {/* Show chevron when there are projects, plus button when no projects */}
            {sortedProjects.length > 0 ? (
              <button
                onClick={toggleProjectsCollapse}
                className="sidebar-btn pl-1 pt-1 pb-1 text-gray-500 hover:text-[#181818] dark:text-[#999999] dark:hover:text-white"
                title={isProjectsCollapsed ? "Expand projects" : "Collapse projects"}
              >
                {isProjectsCollapsed ? (
                  <ChevronRightIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
            ) : (
              <button
                onClick={() => setIsCreateProjectModalOpen(true)}
                className="sidebar-btn pl-1 pt-1 pb-1 text-gray-500 hover:text-[#181818] dark:text-[#999999] dark:hover:text-white"
                title="Create new project"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </button>

        {/* Projects List */}
        {!isProjectsCollapsed && (
          <div>
            {isLoadingProjects ? (
              <div className="space-y-2 px-3">
                <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2 flex-1">
                        <Skeleton height={13} width={120} />
                      </div>
                      <div className="flex items-center -space-x-2">
                        <Skeleton circle height={28} width={28} className="border-2 border-[#EDEDED] dark:border-[#161616]" />
                        <Skeleton circle height={28} width={28} className="border-2 border-[#EDEDED] dark:border-[#161616]" />
                        <Skeleton circle height={28} width={28} className="border-2 border-[#EDEDED] dark:border-[#161616]" />
                      </div>
                    </div>
                  ))}
                </SkeletonTheme>
              </div>
            ) : projectsError ? (
              <div className="px-3 py-2">
                <div className="text-xs text-red-500 dark:text-red-400 text-center">
                  {projectsError}
                </div>
                <button
                  onClick={fetchProjects}
                  className="sidebar-btn w-full mt-2 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  Retry
                </button>
              </div>
            ) : sortedProjects.length > 0 ? (
              <div className="space-y-0.5">
                {(showAllProjects ? sortedProjects : sortedProjects.slice(0, 4)).map((project) => (
                  <div key={project.id} className="relative group">
                    <div
                      className={`sidebar-item group flex items-center w-full text-left py-1.5 rounded-xl cursor-pointer ${
                        isProjectActive(project.id) ? 'bg-[#F3F4F6] dark:bg-[#2a2a2a]' : ''
                      }`}
                      onClick={() => {
                        navigate(`/project/${project.id}`);
                      }}
                      onMouseEnter={(e) => handleProjectMenuEnter(project, e)}
                      onMouseLeave={handleProjectMenuLeave}
                    >
                      <div className="flex items-center justify-between px-3 w-full">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {/* Live chat indicator */}
                          {projectHasLiveChats(project.id) && (
                            <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 animate-pulse"></div>
                          )}
                          <span className="text-[13px] truncate flex-1 min-w-0 transition-all duration-200" style={{ fontFamily: 'var(--chat-font-family)' }}>
                            {project.name}
                          </span>
                        </div>
                        {/* Always render the avatar stack container to maintain consistent height */}
                        <div className="flex-shrink-0 ml-2 min-h-[28px] flex items-center">
                          {project.memberCount > 0 ? (
                            <MemberAvatarStack 
                              members={project.members || []} 
                              memberCount={project.memberCount}
                              maxVisible={3}
                              projectId={project.id}
                              projectTypingUsers={projectTypingUsers}
                              projectRecentlyActive={projectRecentlyActive}
                            />
                          ) : (
                            // Empty placeholder to maintain height consistency
                            <div className="h-7 w-0"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {sortedProjects.length > 4 && (
                  <div className="px-3 py-1">
                    <button
                      onClick={() => toggleShowAllProjects()}
                      className="sidebar-btn text-xs text-gray-500 dark:text-[#999999] hover:text-[#181818] dark:hover:text-white px-2 py-1"
                    >
                      {showAllProjects ? 
                        'Show less' : 
                        `+${sortedProjects.length - 4} more projects`
                      }
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  // Render Clients Section
  const renderClientsSection = () => {
    return (
      <div className="pb-3">
        {/* Clients Header */}
        <button 
          onClick={toggleClientsCollapse}
          className="flex items-center justify-between pl-3 pr-2 py-2 w-full text-left"
        >
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <LuUsers className="w-4 h-4" />
            <span className="text-[13px] font-medium" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Clients
            </span>
          </div>
          
          <div className="flex items-center">
            {sortedClients.length > 0 ? (
              <button
                onClick={toggleClientsCollapse}
                className="sidebar-btn pl-1 pt-1 pb-1 text-gray-500 hover:text-[#181818] dark:text-[#999999] dark:hover:text-white"
                title={isClientsCollapsed ? "Expand clients" : "Collapse clients"}
              >
                {isClientsCollapsed ? (
                  <ChevronRightIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
            ) : (
              <button
                onClick={() => setIsCreateClientModalOpen(true)}
                className="sidebar-btn pl-1 pt-1 pb-1 text-gray-500 hover:text-[#181818] dark:text-[#999999] dark:hover:text-white"
                title="Add new client"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </button>

        {/* Clients List */}
        {!isClientsCollapsed && (
          <div>
            {isLoadingClients ? (
              <div className="space-y-2 px-3">
                <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center py-2">
                      <div className="flex items-center gap-2 flex-1">
                        <Skeleton circle height={24} width={24} />
                        <Skeleton height={13} width={100} />
                      </div>
                    </div>
                  ))}
                </SkeletonTheme>
              </div>
            ) : clientsError ? (
              <div className="px-3 py-2">
                <div className="text-xs text-red-500 dark:text-red-400 text-center">
                  {clientsError}
                </div>
                <button
                  onClick={fetchClients}
                  className="sidebar-btn w-full mt-2 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  Retry
                </button>
              </div>
            ) : sortedClients.length > 0 ? (
              <div className="space-y-0.5">
                {(showAllClients ? sortedClients : sortedClients.slice(0, 6)).map((client) => (
                  <div key={client.id} className="relative group">
                    <div
                      className={`sidebar-item group flex items-center w-full text-left py-1.5 rounded-xl cursor-pointer ${
                        (viewingClient?.id || viewingClient?._id) === (client.id || client._id) ? 'bg-[#F3F4F6] dark:bg-[#2a2a2a]' : ''
                      }`}
                      onClick={() => handleClientClick(client)}
                    >
                      <div className="flex items-center px-3 w-full gap-2">
                        {/* Client color indicator */}
                        <div 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: client.color || '#6366f1' }}
                        />
                        <span 
                          className="text-[13px] truncate flex-1 min-w-0" 
                          style={{ fontFamily: 'var(--chat-font-family)' }}
                        >
                          {client.name}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {sortedClients.length > 6 && (
                  <div className="px-3 py-1">
                    <button
                      onClick={toggleShowAllClients}
                      className="sidebar-btn text-xs text-gray-500 dark:text-[#999999] hover:text-[#181818] dark:hover:text-white px-2 py-1"
                    >
                      {showAllClients ? 
                        'Show less' : 
                        `+${sortedClients.length - 6} more clients`
                      }
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-3 py-3 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  No clients yet
                </p>
                <button
                  onClick={() => setIsCreateClientModalOpen(true)}
                  className="sidebar-btn text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 px-2 py-1"
                >
                  + Add your first client
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderChatList = () => {
    // Client View Mode - Show chats for a specific client
    if (viewingClient) {
      return (
        <div className="flex flex-col h-full">
          {/* Client Header with Back Button */}
          <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-200 dark:border-[#333] mb-2">
            <button
              onClick={handleBackFromClientView}
              className="sidebar-btn p-1"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-sm font-medium truncate" style={{ fontFamily: 'var(--chat-font-family)' }}>
              {viewingClient.name}
            </span>
          </div>

          {/* Client Chats List */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-1 min-w-0">
            {isLoadingClientChats ? (
              <div className="space-y-2 px-2">
                <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl">
                      <Skeleton circle height={8} width={8} />
                      <Skeleton height={13} width={`${Math.floor(Math.random() * 100) + 80}px`} />
                    </div>
                  ))}
                </SkeletonTheme>
              </div>
            ) : clientChats.length > 0 ? (
              <div className="space-y-1">
                {groupChatsByDate(clientChats).map(([date, dateChats], index) => (
                  <div key={date} className={`${date === 'Today' ? 'pb-1' : 'pb-2'} ${index > 0 ? 'pt-1' : ''}`}>
                    <h3 
                      className="text-gray-400 dark:text-[#999999] text-[13px] font-[400] mb-1 px-3" 
                      style={{ fontFamily: 'var(--chat-font-family)' }}
                    >
                      {date}
                    </h3>
                    <div className="space-y-0.5">
                      {dateChats.map((chat) => (
                        <div key={chat._id} className={`relative group ${animatingChats.has(chat._id) ? 'new-chat-animation' : ''}`}>
                          <Link
                            to={`/chat/${chat._id}?client=${viewingClient.id || viewingClient._id}`}
                            className={`sidebar-item group flex items-center w-full text-left py-2 px-3 rounded-xl ${
                              activeChat === chat._id ? 'bg-[#F3F4F6] dark:bg-[#2a2a2a]' : activeMenu === chat._id ? 'bg-[#F6F7F8] dark:bg-[#202020]' : ''
                            }`}
                          >
                            <div className="flex items-center w-full relative">
                              <div className="flex items-center gap-2 w-full group-hover:pr-8 transition-all duration-200">
                                <span className="text-[13px] truncate flex-1 min-w-0 transition-all duration-200" style={{ fontFamily: 'var(--chat-font-family)' }}>
                                  {editingChatId === chat._id ? (
                                    <input
                                      type="text"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={() => handleRenameSubmit(chat._id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleRenameSubmit(chat._id);
                                        }
                                      }}
                                      className="bg-transparent w-full focus:outline-none"
                                      style={{ fontFamily: 'var(--chat-font-family)' }}
                                      autoFocus
                                    />
                                  ) : (
                                    chat.title || 'Untitled Chat'
                                  )}
                                </span>
                              </div>
                              <button
                                onClick={(e) => handleMenuClick(e, chat._id)}
                                className={`sidebar-btn absolute -right-2 transition-opacity text-gray-500 dark:text-[#999999] hover:text-[#181818] p-1 flex-shrink-0 ${activeMenu === chat._id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                              >
                                <EllipsisHorizontalIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </Link>
                          {activeMenu === chat._id && (
                            <ContextMenu
                              isOpen={true}
                              position={menuPosition}
                              onClose={() => setActiveMenu(null)}
                              items={[
                                {
                                  label: 'Rename',
                                  icon: <PencilIcon className="w-4 h-4" />,
                                  onClick: (e) => handleRename(e, chat._id, chat.title || 'Untitled Chat')
                                },
                                {
                                  label: 'Delete',
                                  icon: <TrashIcon className="w-4 h-4" />,
                                  onClick: (e) => handleDelete(e, chat._id),
                                  danger: true
                                }
                              ]}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-[#1e1e1e] flex items-center justify-center mb-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 dark:text-[#666]">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <p
                  className="text-[13px] text-gray-400 dark:text-[#666] mb-3"
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  No chats yet for this client
                </p>
                <button
                  onClick={() => {
                    navigate(`/?client=${viewingClient.id || viewingClient._id}`);
                  }}
                  className="text-[12px] font-medium text-gray-500 dark:text-[#888] hover:text-[#181818] dark:hover:text-gray-200 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-[#444] transition-colors"
                  style={{ fontFamily: 'var(--chat-font-family)' }}
                >
                  Start a conversation
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // 🧪 Test Mode - Show test user chats grouped by user
    if (isTestMode) {
      // Show loading state for test users
      if (testUsersLoading) {
        return (
          <div className="space-y-4">
            <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
              {[...Array(3)].map((_, i) => (
                <div key={i} className="mb-4">
                  {/* Test User Header Skeleton */}
                  <div className="flex items-center justify-between px-2 py-2 rounded-lg">
                    <div className="flex items-center gap-2 flex-1">
                      <Skeleton circle height={24} width={24} />
                      <div className="flex flex-col">
                        <Skeleton height={14} width={100} />
                        <Skeleton height={12} width={80} />
                      </div>
                    </div>
                    <Skeleton height={16} width={16} />
                  </div>
                  
                  {/* User Chats Skeleton */}
                  <div className="ml-4 space-y-1">
                    {[...Array(2)].map((_, j) => (
                      <div key={j} className="flex items-center gap-2 px-3 py-2 rounded-lg">
                        <Skeleton circle height={8} width={8} />
                        <Skeleton height={14} width={150} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </SkeletonTheme>
          </div>
        );
      }

      // Show test user chats grouped by user
      const testUserNames = Object.keys(testUserChats);
      if (testUserNames.length === 0) {
        return (
          <div className="text-gray-500 dark:text-[#999999] text-center mt-4">
            No test user chats yet
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {/* Active Test User Indicator */}
          {activeTestUser && (
            <div className="px-2 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">🎭</span>
                  </div>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Impersonating: {activeTestUser}
                  </span>
                </div>
                <button
                  onClick={clearTestUser}
                  className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          
          {testUserNames.map(userName => {
            const userData = testUserChats[userName];
            const isCollapsed = collapsedTestUsers.has(userName);
            const isActive = activeTestUser === userName;
            const userChats = userData.chats || [];
            
            return (
              <div key={userName} className="mb-4">
                {/* Test User Header */}
                <div 
                  className={`flex items-center justify-between px-2 py-2 cursor-pointer rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <div 
                    className="flex items-center gap-2 flex-1"
                    onClick={() => switchToTestUser(userName)}
                  >
                    {/* User Avatar */}
                    <div 
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        isActive ? 'ring-2 ring-blue-400 dark:ring-blue-500' : ''
                      }`}
                      style={{ backgroundColor: userData.color }}
                    >
                      {userName.split(' ').map(n => n[0]).join('')}
                    </div>
                    
                    {/* User Name & Role */}
                    <div className="flex flex-col">
                      <span className={`text-sm font-semibold ${
                        isActive 
                          ? 'text-blue-800 dark:text-blue-200' 
                          : 'text-gray-800 dark:text-gray-200'
                      }`}>
                        {userName} {isActive && '(Active)'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-[#999999]">
                        {userData.role} • {userChats.length} chats
                      </span>
                    </div>
                  </div>
                  
                  {/* Collapse Icon */}
                  <div 
                    className="text-gray-400 dark:text-gray-500 p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTestUserCollapse(userName);
                    }}
                  >
                    {isCollapsed ? (
                      <ChevronRightIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </div>
                </div>
                
                {/* User Chats */}
                {!isCollapsed && (
                  <div className="ml-4 space-y-1">
                    {userChats.map((chat, index) => (
                      <div 
                        key={chat._id || index}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          activeChat === chat._id
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => {
                          onChatSelect(chat._id);
                          // Navigate with proper query params for project/client chats
                          const params = chat.projectId ? `?project=${chat.projectId}` : chat.clientId ? `?client=${chat.clientId}` : '';
                          navigate(`/chat/${chat._id}${params}`);
                        }}
                      >
                        {/* Chat Icon */}
                        <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                        
                        {/* Chat Title */}
                        <span className="text-sm truncate">
                          {chat.title || 'Untitled Chat'}
                        </span>
                        
                        {/* Chat Menu */}
                        <div 
                          className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => handleMenuClick(e, chat._id)}
                        >
                          <EllipsisHorizontalIcon className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Regular Mode - Show date-grouped chats
    if (isLoading) {
      return (
        <div className="space-y-1">
          <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
            {/* Today section skeleton */}
            <div className="pb-1">
              <div className="px-3 mb-1">
                <Skeleton height={13} width={40} />
              </div>
              <div className="space-y-0.5 mb-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="px-3 py-2 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Skeleton height={13} width={`${Math.floor(Math.random() * 100) + 120}px`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Yesterday section skeleton */}
            <div className="pb-2 pt-1">
              <div className="px-3 mb-1">
                <Skeleton height={13} width={60} />
              </div>
              <div className="space-y-0.5 mb-2">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="px-3 py-2 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Skeleton height={13} width={`${Math.floor(Math.random() * 100) + 120}px`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SkeletonTheme>
        </div>
      );
    }

    // Check if there are any chats to display
    const groupedChatsToDisplay = groupChatsByDate(filteredChats);
    
    if (groupedChatsToDisplay.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full px-6 text-center">
          <p
            className="text-[13px] text-gray-400 dark:text-[#555]"
            style={{ fontFamily: 'var(--chat-font-family)' }}
          >
            Your conversations will appear here
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {groupedChatsToDisplay.map(([date, dateChats], index) => (
          <div key={date} className={`${date === 'Today' ? 'pb-1' : 'pb-2'} ${index > 0 ? 'pt-1' : ''}`}>
            <h3 
              className="text-gray-400 dark:text-[#999999] text-[13px] font-[400] mb-1 px-3" 
              style={{ fontFamily: 'var(--chat-font-family)' }}
            >
              {date}
            </h3>
            <div className="space-y-0.5 mb-2">
              {dateChats.map((chat) => (
                <div 
                  key={chat._id} 
                  className={`relative group ${
                    animatingChats.has(chat._id) ? 'new-chat-animation' : ''
                  }`}
                >
                  <Link
                    to={`/chat/${chat._id}${chat.projectId ? `?project=${chat.projectId}` : chat.clientId ? `?client=${chat.clientId}` : ''}`}
                    className={`sidebar-item group flex items-center w-full text-left py-2 px-3 rounded-xl ${
                      isChatActive(chat._id) ? 'bg-[#F3F4F6] dark:bg-[#2a2a2a]' : activeMenu === chat._id ? 'bg-[#F6F7F8] dark:bg-[#202020]' : ''
                    }`}
                    onMouseEnter={() => debouncedPrefetch(chat._id)}
                  >
                    <div className="flex items-center w-full relative">
                      <div className="flex items-center gap-2 w-full group-hover:pr-8 transition-all duration-200">
                        {/* SuperBucket Icon */}
                        {superBucketChats.has(chat._id) && (
                          <FaSuperpowers 
                            className="w-3 h-3 flex-shrink-0 text-indigo-400" 
                            title="SuperBucket Chat"
                          />
                        )}
                        {/* Show client color dot for client chats */}
                        {chat.clientId && (
                          <div 
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: getClientColorForChat(chat) }}
                          />
                        )}
                        <span className="text-[13px] truncate flex-1 min-w-0 transition-all duration-200" style={{ fontFamily: 'var(--chat-font-family)' }}>
                          {editingChatId === chat._id ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => handleRenameSubmit(chat._id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleRenameSubmit(chat._id);
                                }
                              }}
                              className="bg-transparent w-full focus:outline-none"
                              style={{ fontFamily: 'var(--chat-font-family)' }}
                              autoFocus
                            />
                          ) : (
                            formatChatTitle(chat)
                          )}
                        </span>
                      </div>
                      <button
                        onClick={(e) => handleMenuClick(e, chat._id)}
                        className={`sidebar-btn absolute -right-2 transition-opacity text-gray-500 dark:text-[#999999] hover:text-[#181818] p-1 flex-shrink-0 ${activeMenu === chat._id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      >
                        <EllipsisHorizontalIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </Link>
                  {activeMenu === chat._id && (
                    <ContextMenu
                      isOpen={true}
                      position={menuPosition}
                      onClose={() => setActiveMenu(null)}
                      items={[
                        {
                          label: 'Rename',
                          icon: <PencilIcon className="w-4 h-4" />,
                          onClick: (e) => handleRename(e, chat._id, formatChatTitle(chat))
                        },
                        {
                          label: 'Delete',
                          icon: <TrashIcon className="w-4 h-4" />,
                          onClick: (e) => handleDelete(e, chat._id),
                          danger: true
                        }
                      ]}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {(hasMore || isPaginationLoading) && (
          <div 
            ref={loadMoreRef}
            className="py-4"
          >
            {isPaginationLoading && (
              <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
                <div className="space-y-0.5">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="px-3 py-2 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Skeleton height={13} width={`${Math.floor(Math.random() * 100) + 120}px`} />
                      </div>
                    </div>
                  ))}
                </div>
              </SkeletonTheme>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div 
        ref={sidebarRef}
        className={`sidebar-root flex flex-col h-screen transition-all duration-300 ${!internalIsCollapsed ? 'border-r border-[#E5E7EB] dark:border-[#282828]' : ''} ${internalIsCollapsed ? 'bg-transparent dark:bg-transparent w-0 min-w-0' : 'bg-[#F9FAFB] dark:bg-[#161616]'} text-black dark:text-gray-200 relative overflow-visible`} 
        style={{ 
          width: internalIsCollapsed ? 0 : `${sidebarWidth}px`,
          fontFamily: 'var(--chat-font-family)'
        }}
      >
        {/* Inner clip wrapper — keeps content from spilling while allowing the resize handle to extend beyond */}
        <div className={`flex flex-col flex-1 min-h-0 w-full ${internalIsCollapsed ? 'overflow-visible' : 'overflow-hidden'}`}>
        {/* Top section with logo and buttons */}
        <div className="flex pt-1.5">
          {/* Logo in its own box */}
          <div className="w-auto flex items-center justify-center py-3 px-5 max-[1200px]:py-2.5 max-[1200px]:px-3 max-[900px]:py-2 max-[900px]:px-2.5 [@media(max-height:800px)]:py-2.5 [@media(max-height:800px)]:px-3 [@media(max-height:700px)]:py-2 [@media(max-height:700px)]:px-2.5">
            {isLogoLoading ? (
              <LogoSpinner darkMode={darkMode} />
            ) : (
              <img 
                src={logoUrl} 
                alt="Company Logo" 
                className={getLogoClassName()}
                onError={() => {
                  // Fall back to default logo if custom logo fails to load
                  setLogoUrl(darkMode ? nvgtrBMDark : nvgtrBM);
                  setIsUsingCustomAssets(false);
                  setCurrentLogoPreference('brandmark');
                  setIsLogoLoading(false);
                }}
                onLoad={() => {
                  // Ensure loading state is cleared when image loads
                  setIsLogoLoading(false);
                }}
              />
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex-1 flex items-center justify-end gap-1 px-2 max-[1200px]:gap-1 max-[900px]:gap-0.5 [@media(max-height:800px)]:gap-1 [@media(max-height:700px)]:gap-0.5">
            {/* 🧪 Test Mode Indicator */}
            {isTestMode && (
              <div className="px-2 py-1 text-xs rounded bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 font-medium">
                🧪 Test Mode
              </div>
            )}
            
            {/* Collapse button */}
            <div className="relative group">
              <button
                onClick={handleToggleCollapse}
                className="sidebar-btn w-8 h-8 flex items-center justify-center text-[#444444] dark:text-[#aaaaaa] hover:text-[#181818] dark:hover:text-gray-200 max-[1200px]:w-7 max-[1200px]:h-7 max-[900px]:w-6 max-[900px]:h-6 [@media(max-height:800px)]:w-7 [@media(max-height:800px)]:h-7 [@media(max-height:700px)]:w-6 [@media(max-height:700px)]:h-6"
              >
                {internalIsCollapsed ? (
                  <TbLayoutSidebarRightCollapse className="w-5 h-5 max-[1200px]:w-4 max-[1200px]:h-4 max-[900px]:w-3.5 max-[900px]:h-3.5 [@media(max-height:800px)]:w-4 [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:w-3.5 [@media(max-height:700px)]:h-3.5" />
                ) : (
                  <TbLayoutSidebarRightExpand className="w-5 h-5 max-[1200px]:w-4 max-[1200px]:h-4 max-[900px]:w-3.5 max-[900px]:h-3.5 [@media(max-height:800px)]:w-4 [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:w-3.5 [@media(max-height:700px)]:h-3.5" />
                )}
              </button>
              
                             {/* Tooltip */}
               <div className="absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2 opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform group-hover:-translate-x-1">
                 <div className="sidebar-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg">
                   {internalIsCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                   <div className="absolute -right-[6px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tr-[4px] border-t border-r border-[#dbdbdb] dark:border-[#2a2a2a] transform rotate-45"></div>
                 </div>
               </div>
            </div>
            
            {/* New chat button - + creates new chat directly (dropdown commented out for now) */}
            <div className="relative group">
              <button
                onClick={handleNewChat}
                title="New Chat"
                className="sidebar-btn flex items-center justify-center px-3 py-1.5 text-[#444444] dark:text-[#aaaaaa] hover:text-[#181818] dark:hover:text-gray-200 border border-transparent hover:border-[#dbdbdb] dark:hover:border-[#2a2a2a]"
              >
                <FaPlus className="w-4 h-4 max-[1200px]:w-3.5 max-[1200px]:h-3.5 max-[900px]:w-3 max-[900px]:h-3" />
              </button>
              {/* Tooltip - temporary until modal returns */}
              <div className="absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2 opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform group-hover:-translate-x-1">
                <div className="sidebar-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg">
                  New Chat
                  <div className="absolute -right-[6px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tr-[4px] border-t border-r border-[#dbdbdb] dark:border-[#2a2a2a] transform rotate-45"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search nav item */}
        <div className="px-2 mt-2">
          <button
            onClick={() => setIsSearchModalOpen(true)}
            className="sidebar-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left"
          >
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-700 dark:text-gray-300 flex-shrink-0 stroke-[2.25]" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" style={{ fontFamily: 'var(--chat-font-family)' }}>
              Search
            </span>
          </button>
        </div>
        
        {/* Clients Section - COMMENTED OUT
        {user?.companyId && (
          <div className="px-2 pt-2">
            {renderClientsSection()}
          </div>
        )}
        */}
        
        {/* Projects Section - COMMENTED OUT for Clients feature
        {user?.companyId && canAccessProjects(user) && (
          <div className="px-2 pt-2">
            {renderProjectsSection()}
          </div>
        )}
        */}
        
        {/* Chat List */}
        <div
          className="flex-1 px-2 pt-4 overflow-y-auto overflow-x-hidden scrollbar-hide hover:scrollbar-default min-w-0"
          style={{ fontFamily: 'var(--chat-font-family)' }}
        >
          {error && (
            <div className="my-2 px-3 py-2 bg-[#FEE2E2] dark:bg-[#3a1717] border border-[#ff6b6b] rounded-xl flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 text-[#ff6b6b] dark:text-[#ff6b6b]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <span className="text-xs text-[#ff6b6b] dark:text-[#ff6b6b]">{error}</span>
              </div>
              <button 
                onClick={() => fetchChats(false)}
                className="sidebar-btn p-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-[#EF4444] dark:text-[#ff6b6b]">
                  <path fillRule="evenodd" d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          {renderChatList()}
        </div>

      

        {/* User profile at bottom */}
        <div className="p-2 max-[1200px]:py-1.5 max-[900px]:py-1 [@media(max-height:800px)]:py-1.5 [@media(max-height:700px)]:py-1">
          <div 
            ref={profileButtonRef}
            className={`sidebar-item flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl max-[1200px]:py-2 max-[900px]:py-1.5 [@media(max-height:800px)]:py-2 [@media(max-height:700px)]:py-1.5
              ${isAccountModalOpen ? 'bg-[#F3F4F6] dark:bg-[#282828]' : ''}`}
            onClick={() => {
              onChatSelect(null);
              window.dispatchEvent(new CustomEvent('open-account-modal', { detail: { tab: 'account' } }));
            }}
            onMouseEnter={handleProfileMenuEnter}
            onMouseLeave={handleProfileMenuLeave}
          >
            <div className="w-8 h-8 rounded-full bg-[#EDEDED] dark:bg-[#333] flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 overflow-hidden max-[1200px]:w-7 max-[1200px]:h-7 max-[900px]:w-6 max-[900px]:h-6 [@media(max-height:800px)]:w-7 [@media(max-height:800px)]:h-7 [@media(max-height:700px)]:w-6 [@media(max-height:700px)]:h-6">
              {user?.profileImage?.url ? (
                <img 
                  src={user.profileImage.url} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
              ) : (
                <span>
                  {user?.firstName && user?.lastName ? 
                    `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` 
                    : 'U'}
                </span>
              )}
            </div>
            {!internalIsCollapsed && (
              <span className="text-sm font-medium text-[#181818] dark:text-gray-300" style={{ fontFamily: 'var(--chat-font-family)' }}>
                {user?.firstName && user?.lastName ? 
                  `${user.firstName} ${user.lastName}` 
                  : 'User'}
              </span>
            )}
          </div>
        </div>
        
        </div>{/* end inner clip wrapper */}

        {/* Resize handle — sits outside the overflow-hidden inner wrapper so both sides are grabbable */}
        {!internalIsCollapsed && (
          <div 
            className="sidebar-resize-handle"
            onMouseDown={startResizing}
          />
        )}
      </div>

      {/* Expand button - only visible when sidebar is collapsed */}
      {internalIsCollapsed && (
        <div className="absolute top-[15px] left-[15px] z-50 group">
          <button
            onClick={handleToggleCollapse}
            className="sidebar-btn w-8 h-8 flex items-center justify-center text-[#444444] dark:text-[#aaaaaa] hover:text-[#181818] dark:hover:text-gray-200 max-[1200px]:w-7 max-[1200px]:h-7 max-[900px]:w-6 max-[900px]:h-6 [@media(max-height:800px)]:w-7 [@media(max-height:800px)]:h-7 [@media(max-height:700px)]:w-6 [@media(max-height:700px)]:h-6"
          >
            <TbLayoutSidebarRightCollapse className="w-5 h-5 max-[1200px]:w-4 max-[1200px]:h-4 max-[900px]:w-3.5 max-[900px]:h-3.5 [@media(max-height:800px)]:w-4 [@media(max-height:800px)]:h-4 [@media(max-height:700px)]:w-3.5 [@media(max-height:700px)]:h-3.5" />
          </button>
          
          {/* Tooltip */}
          <div className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform group-hover:translate-x-1">
                         <div className="sidebar-tooltip px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap relative shadow-lg">
               Open sidebar
                             <div className="absolute -left-[6px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tl-[4px] border-t border-l border-[#dbdbdb] dark:border-[#2a2a2a] transform -rotate-45"></div>
            </div>
          </div>
        </div>
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onChatSelect={onChatSelect}
        onNewChat={handleNewChat}
        darkMode={darkMode}
      />

      {/* Delete confirmation modal */}
      <Modal 
        isOpen={deleteModalOpen} 
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete chat"
        message="Are you sure you want to delete this chat? Any context derived from this chat will be retained."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* Create Client Modal */}
      <CreateClientModal
        isOpen={isCreateClientModalOpen}
        onClose={() => setIsCreateClientModalOpen(false)}
        onClientCreated={handleClientCreated}
      />

      {/* Plus button hover menu - COMMENTED OUT: + now creates new chat directly
      {isPlusMenuOpen && !internalIsCollapsed && canAccessProjects(user) && (
        <div 
          className="fixed z-50"
          style={{
            left: `${plusMenuPosition.x}px`,
            top: `${plusMenuPosition.y}px`,
          }}
          onMouseEnter={handlePlusMenuEnter}
          onMouseLeave={handlePlusMenuLeave}
        >
          <div className="bg-white dark:bg-[#161616] rounded-xl shadow-lg border border-gray-200 dark:border-[#2a2a2a] py-1.5 px-1.5 w-48">
            <button
              onClick={handleNewChat}
              className="w-full text-left px-3 py-2.5 flex items-center gap-3 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
              style={{ fontFamily: 'var(--chat-font-family)' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0">
                <path fill="currentColor" fillRule="evenodd" d="M6.169 6.331a3 3 0 0 0-.833 1.6l-.338 1.912a1 1 0 0 0 1.159 1.159l1.912-.338a3 3 0 0 0 1.6-.833l3.07-3.07l2-2A.9.9 0 0 0 15 4.13A3.13 3.13 0 0 0 11.87 1a.9.9 0 0 0-.632.262l-2 2zm3.936-1.814L7.229 7.392a1.5 1.5 0 0 0-.416.8L6.6 9.4l1.208-.213l.057-.01a1.5 1.5 0 0 0 .743-.406l2.875-2.876a1.63 1.63 0 0 0-1.378-1.378m2.558.199a3.14 3.14 0 0 0-1.379-1.38l.82-.82a1.63 1.63 0 0 1 1.38 1.38zM8 2.25a.75.75 0 0 0-.75-.75H4.5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h7a3 3 0 0 0 3-3V8.75a.75.75 0 0 0-1.5 0v2.75a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7A1.5 1.5 0 0 1 4.5 3h2.75A.75.75 0 0 0 8 2.25" clipRule="evenodd"/>
              </svg>
              <span className="text-[15px] font-semibold text-gray-900 dark:text-white">New Chat</span>
            </button>
            <button
              onClick={handleNewClient}
              className="w-full text-left px-3 py-2.5 flex items-center gap-3 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e]"
              style={{ fontFamily: 'var(--chat-font-family)' }}
            >
              <UserPlusIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <span className="text-[15px] font-semibold text-gray-900 dark:text-white">New Client</span>
            </button>
            {canAccessProjects(user) && (
              <button
                onClick={handleNewProject}
                className="w-full text-left px-3 py-2 text-sm rounded-lg text-[#181818] dark:text-gray-200 hover:bg-[#F6F7F8] dark:hover:bg-[#2A2A2A] transition-colors flex items-center gap-3"
                style={{ fontFamily: 'var(--chat-font-family)' }}
              >
                <LuFolderPlus className="w-4 h-4" />
                New Project
              </button>
            )}
          </div>
        </div>
      )}
      */}

      {/* Project hover menu - matches model selector style */}
      {isProjectMenuOpen && hoveredProject && !internalIsCollapsed && (
        <div 
          className="fixed z-50"
          style={{
            left: `${projectMenuPosition.x}px`,
            top: `${projectMenuPosition.y}px`,
          }}
          onMouseEnter={() => {
            // Clear timeout when mouse enters menu
            if (projectMenuTimeoutRef.current) {
              clearTimeout(projectMenuTimeoutRef.current);
              projectMenuTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleProjectMenuLeave}
        >
          <div className="bg-white dark:bg-[#161616] rounded-xl shadow-lg border border-gray-200 dark:border-[#2a2a2a] py-1.5 px-1.5 w-56">
            <button
              onClick={() => handleGoToProject(hoveredProject.id)}
              className="w-full text-left px-3 py-2.5 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-[#1e1e1e] flex items-center relative pr-8"
              style={{ fontFamily: 'var(--chat-font-family)' }}
            >
              <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Go to Project</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 absolute right-3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
            
            {/* Divider */}
            <div className="my-2 border-t border-gray-200 dark:border-[#2a2a2a]"></div>
            
            {/* Members list */}
            <div className="max-h-48 overflow-y-auto scrollbar-hide">
              <div className="px-3 py-1 text-xs text-gray-500 dark:text-[#999999] font-medium flex items-center gap-2">
                Collaborators 
                <span className="bg-[#C3C3C3] dark:bg-[#444444] text-[#181818] dark:text-[#EDEDED] w-5 h-5 rounded-full text-[10px] font-medium flex items-center justify-center leading-none">
                  {hoveredProject.memberCount}
                </span>
              </div>
              {hoveredProject.members && hoveredProject.members.length > 0 ? (
                <div className="space-y-1">
                  {hoveredProject.members.map((member) => {
                    // Get member initials using the same logic as avatar stack
                    const getMemberInitials = (member) => {
                      if (member?.firstName && member?.lastName) {
                        return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
                      } else if (member?.name) {
                        const nameParts = member.name.trim().split(' ');
                        if (nameParts.length >= 2) {
                          return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                        } else {
                          return nameParts[0].charAt(0).toUpperCase();
                        }
                      }
                      return 'U';
                    };

                    // Get user color using the same logic as avatar stack
                    const pastelColors = [
                      { bg: 'bg-[#F5C8BD]', hex: '#F5C8BD' },
                      { bg: 'bg-[#E9BACC]', hex: '#E9BACC' },
                      { bg: 'bg-[#D0AAB0]', hex: '#D0AAB0' },
                      { bg: 'bg-[#B599C1]', hex: '#B599C1' },
                      { bg: 'bg-[#ABACCB]', hex: '#ABACCB' },
                      { bg: 'bg-[#9FBFDD]', hex: '#9FBFDD' },
                      { bg: 'bg-[#97CCDC]', hex: '#97CCDC' },
                      { bg: 'bg-[#83C4B4]', hex: '#83C4B4' },
                      { bg: 'bg-[#A9D194]', hex: '#A9D194' },
                      { bg: 'bg-[#D5E4AD]', hex: '#D5E4AD' },
                      { bg: 'bg-[#FAEEAE]', hex: '#FAEEAE' },
                      { bg: 'bg-[#F5BE6B]', hex: '#F5BE6B' }
                    ];

                    // Function to generate a darker text color based on the background color
                    const getDarkerTextColor = (hexColor) => {
                      // Remove the # if present
                      const hex = hexColor.replace('#', '');
                      
                      // Parse RGB values
                      const r = parseInt(hex.substr(0, 2), 16);
                      const g = parseInt(hex.substr(2, 2), 16);
                      const b = parseInt(hex.substr(4, 2), 16);
                      
                      // Generate darker versions (reduce each component by about 40-60%)
                      const darkerR = Math.max(0, Math.floor(r * 0.4));
                      const darkerG = Math.max(0, Math.floor(g * 0.4));
                      const darkerB = Math.max(0, Math.floor(b * 0.4));
                      
                      // Convert back to hex
                      const toHex = (n) => n.toString(16).padStart(2, '0');
                      return `#${toHex(darkerR)}${toHex(darkerG)}${toHex(darkerB)}`;
                    };

                    const getUserColor = (userId) => {
                      if (!userId) return pastelColors[0];
                      
                      let hash = 0;
                      for (let i = 0; i < userId.length; i++) {
                        const char = userId.charCodeAt(i);
                        hash = ((hash << 5) - hash) + char;
                        hash = hash & hash;
                      }
                      
                      return pastelColors[Math.abs(hash) % pastelColors.length];
                    };

                    const userColor = getUserColor(member.id);
                    const initials = getMemberInitials(member);
                    const hasProfileImage = member?.profileImage?.url;
                    const memberName = member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown Member';

                    return (
                      <div key={member.id} className="flex items-center px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1e1e1e] transition-colors">
                        {/* Member avatar */}
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden relative mr-3 flex-shrink-0">
                          {hasProfileImage ? (
                            // Profile picture with colored outline
                            <div className="w-full h-full rounded-full overflow-hidden relative">
                              <div 
                                className="absolute inset-0 rounded-full"
                                style={{ backgroundColor: userColor.hex }}
                              />
                              <div className="absolute inset-[2px] rounded-full overflow-hidden bg-white dark:bg-[#161616]">
                                <img 
                                  src={member.profileImage.url} 
                                  alt={memberName}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // Try the proxy URL if the original fails
                                    const proxiedUrl = getProxiedImageUrl(member.profileImage.url);
                                    if (proxiedUrl && proxiedUrl !== e.target.src) {
                                      console.log('S3 URL failed, trying proxy for member:', memberName);
                                      e.target.src = proxiedUrl;
                                    } else {
                                      // Both failed - hide image
                                      console.error('Both S3 URL and proxy failed for member:', memberName);
                                      e.target.style.display = 'none';
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            // Monogram with colored background
                            <div 
                              className={`w-full h-full flex items-center justify-center rounded-full`}
                              style={{ 
                                backgroundColor: userColor.hex,
                                color: getDarkerTextColor(userColor.hex)
                              }}
                            >
                              {initials}
                            </div>
                          )}
                        </div>
                        
                        {/* Member name */}
                        <span className="text-[13px] text-[#181818] dark:text-gray-200 truncate" style={{ fontFamily: 'var(--chat-font-family)' }}>
                          {memberName}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-3 py-2 text-[13px] text-gray-500 dark:text-[#999999]">
                  No members to display
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile hover menu */}
      {/* {isProfileMenuOpen && !internalIsCollapsed && (
        <div 
          className="fixed z-50"
          style={{
            left: `${profileMenuPosition.x}px`,
            top: `${profileMenuPosition.y}px`,
          }}
          onMouseEnter={() => {
            // Clear timeout when mouse enters menu
            if (profileMenuTimeoutRef.current) {
              clearTimeout(profileMenuTimeoutRef.current);
              profileMenuTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleProfileMenuLeave}
        >
          {/* Pointer tip */}
          {/* <div className="absolute -left-[6px] bottom-6 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tl-[4px] border-t border-l border-[#dbdbdb] dark:border-[#2a2a2a] transform -rotate-45"></div>
          
          {/* Menu content */}
          {/* <div className="bg-[#EDEDED] dark:bg-[#161616] rounded-xl shadow-lg px-2 py-2 w-auto border border-[#dbdbdb] dark:border-[#2a2a2a]">
            {/* Menu items */}
            {/* <div className="space-y-1">
              <button
                onClick={() => handleProfileMenuClick('account')}
                className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#F6F7F8] dark:hover:bg-[#3a3a3a] transition-colors text-left"
              >
                <UserIcon className="w-4 h-4 mr-3 text-gray-500 dark:text-[#999999]" />
                <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  Profile
                </span>
              </button>
              <button
                onClick={() => handleProfileMenuClick('appearance')}
                className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#F6F7F8] dark:hover:bg-[#3a3a3a] transition-colors text-left"
              >
                <SunIcon className="w-4 h-4 mr-3 text-gray-500 dark:text-[#999999]" />
                <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  Appearance
                </span>
              </button>
              <button
                onClick={() => handleProfileMenuClick('billing')}
                className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#F6F7F8] dark:hover:bg-[#3a3a3a] transition-colors text-left"
              >
                <CurrencyDollarIcon className="w-4 h-4 mr-3 text-gray-500 dark:text-[#999999]" />
                <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  Billing
                </span>
              </button>
              <button
                onClick={() => handleProfileMenuClick('organization')}
                className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#F6F7F8] dark:hover:bg-[#3a3a3a] transition-colors text-left"
              >
                <UsersIcon className="w-4 h-4 mr-3 text-gray-500 dark:text-[#999999]" />
                <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  Organization
                </span>
              </button>
              
              {/* Divider */}
              {/* <div className="my-2 border-t border-gray-200 dark:border-[#2a2a2a]"></div>
              
              <button
                onClick={() => handleProfileMenuClick('settings')}
                className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#F6F7F8] dark:hover:bg-[#3a3a3a] transition-colors text-left"
              >
                <Cog6ToothIcon className="w-4 h-4 mr-3 text-gray-500 dark:text-[#999999]" />
                <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  Settings
                </span>
              </button>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
});

export default Sidebar;